<template>
    <div class="payout-detail__content payout-banker-permutation">
        <div class="payout-banker-permutation__header">
            <h2>{{ getTranslatedText('Banker Bets & Permutations Prize Tiers') }}</h2>
        </div>

        <!-- Three Column Layout -->
        <div class="payout-banker-permutation__layout">
            <!-- LEFT SIDEBAR - Banker Buttons -->
            <div class="payout-banker-permutation__sidebar">
                <div class="payout-banker-permutation__sidebar-header">
                    <h3>BANKER</h3>
                </div>
                <div class="payout-banker-permutation__sidebar-buttons">
                    <button 
                        v-for="(game, key) in bankerGames" 
                        :key="'banker-' + key"
                        :class="['payout-banker-permutation__btn', { active: activeGame === key && activeType === 'banker' }]"
                        @click="selectGame('banker', key)"
                    >
                        {{ game.title.replace('Banker - ', '') }}
                    </button>
                </div>
            </div>

            <!-- MIDDLE SIDEBAR - Permutation Buttons -->
            <div class="payout-banker-permutation__sidebar payout-banker-permutation__sidebar--permutation">
                <div class="payout-banker-permutation__sidebar-header payout-banker-permutation__sidebar-header--permutation">
                    <h3>PERMUTATION</h3>
                </div>
                <div class="payout-banker-permutation__sidebar-buttons">
                    <button 
                        v-for="(game, key) in permutationGames" 
                        :key="'perm-' + key"
                        :class="['payout-banker-permutation__btn', { active: activeGame === key && activeType === 'permutation' }]"
                        @click="selectGame('permutation', key)"
                    >
                        {{ game.title.replace('Permutation - ', '') }}
                    </button>
                </div>
            </div>

            <!-- RIGHT CONTENT - Prize Tables -->
            <div class="payout-banker-permutation__content">
                <!-- BANKER Game Content Panels -->
                <transition name="slide-fade">
                    <div v-if="activeGame && activeType === 'banker'" class="payout-banker-permutation__panel">
                        <div class="payout-banker-permutation__panel-header">
                            <h3>{{ getTranslatedText(bankerGames[activeGame].title) }}</h3>
                        </div>

                        <!-- Odds Table -->
                        <div class="payout-banker-permutation__section">
                            <h4>{{ getTranslatedText('Odds Table') }}</h4>
                            
                            <!-- Two-column odds table for DC games (First 5 / Last 5) -->
                            <table v-if="bankerGames[activeGame].hasDualOdds" class="payout-banker-permutation__table odds-table dual-odds">
                                <thead>
                                    <tr>
                                        <th>{{ getTranslatedText('Match') }}</th>
                                        <th>{{ getTranslatedText('First 5 Numbers') }}</th>
                                        <th>{{ getTranslatedText('Last 5 Numbers') }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(odd, idx) in bankerGames[activeGame].odds" :key="idx">
                                        <td>{{ getTranslatedText(odd.match) }}</td>
                                        <td class="highlight">{{ odd.first5 }}x</td>
                                        <td class="highlight">{{ odd.last5 }}x</td>
                                    </tr>
                                </tbody>
                            </table>

                            <!-- Single column odds table for regular games -->
                            <table v-else class="payout-banker-permutation__table odds-table">
                                <thead>
                                    <tr>
                                        <th>{{ getTranslatedText('Match') }}</th>
                                        <th>{{ getTranslatedText('Multiplier') }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(odd, idx) in bankerGames[activeGame].odds" :key="idx">
                                        <td>{{ getTranslatedText(odd.match) }}</td>
                                        <td class="highlight">{{ odd.multiplier }}x</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- Banker Against All (if applicable) -->
                        <div v-if="bankerGames[activeGame].bankerAgainstAll" class="payout-banker-permutation__section">
                            <h4>{{ getTranslatedText('Banker One Against All') }} (1 Fix vs 89)</h4>
                            <div class="payout-banker-permutation__info-grid">
                                <div class="info-item">
                                    <span class="label">{{ getTranslatedText('Cost') }}</span>
                                    <span class="value">{{ bankerGames[activeGame].bankerAgainstAll.cost }} FCFA</span>
                                </div>
                                <div class="info-item">
                                    <span class="label">{{ getTranslatedText('Combinations') }}</span>
                                    <span class="value">{{ bankerGames[activeGame].bankerAgainstAll.combinations }}</span>
                                </div>
                                <div class="info-item">
                                    <span class="label">{{ getTranslatedText('Max Prize Lines') }}</span>
                                    <span class="value">{{ bankerGames[activeGame].bankerAgainstAll.maxLines }}</span>
                                </div>
                                <div class="info-item highlight">
                                    <span class="label">{{ getTranslatedText('Gross Max') }}</span>
                                    <span class="value">{{ formatNumber(bankerGames[activeGame].bankerAgainstAll.grossMax) }} FCFA</span>
                                </div>
                                <div class="info-item">
                                    <span class="label">{{ getTranslatedText('Net Max') }}</span>
                                    <span class="value">{{ formatNumber(bankerGames[activeGame].bankerAgainstAll.netMax) }} FCFA</span>
                                </div>
                            </div>
                        </div>

                        <!-- Fix Numbers Banker Table -->
                        <div v-if="bankerGames[activeGame].fixBankerTable" class="payout-banker-permutation__section">
                            <h4>{{ bankerGames[activeGame].fixBankerTable.fixCount }} {{ getTranslatedText('Fix Number(s) Banker') }}</h4>
                            <table class="payout-banker-permutation__table">
                                <thead>
                                    <tr>
                                        <th>{{ getTranslatedText('Support') }}</th>
                                        <th>{{ getTranslatedText('Combos') }}</th>
                                        <th>{{ getTranslatedText('Cost (FCFA)') }}</th>
                                        <th>{{ getTranslatedText('Max Lines') }}</th>
                                        <th>{{ getTranslatedText('Gross Max (FCFA)') }}</th>
                                        <th>{{ getTranslatedText('Net Max (FCFA)') }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(row, idx) in bankerGames[activeGame].fixBankerTable.rows" :key="idx">
                                        <td>{{ row.support }}</td>
                                        <td>{{ row.combos }}</td>
                                        <td>{{ formatNumber(row.cost) }}</td>
                                        <td>{{ row.maxLines }}</td>
                                        <td class="highlight">{{ formatNumber(row.grossMax) }}</td>
                                        <td>{{ formatNumber(row.netMax) }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </transition>

                <!-- PERMUTATION Game Content Panels -->
                <transition name="slide-fade">
                    <div v-if="activeGame && activeType === 'permutation'" class="payout-banker-permutation__panel">
                        <div class="payout-banker-permutation__panel-header payout-banker-permutation__panel-header--permutation">
                            <h3>{{ getTranslatedText(permutationGames[activeGame].title) }}</h3>
                        </div>

                        <!-- Odds Table -->
                        <div class="payout-banker-permutation__section">
                            <h4>{{ getTranslatedText('Odds Table') }}</h4>
                            
                            <!-- Two-column odds table for DC games (First 5 / Last 5) -->
                            <table v-if="permutationGames[activeGame].hasDualOdds" class="payout-banker-permutation__table odds-table dual-odds">
                                <thead>
                                    <tr>
                                        <th>{{ getTranslatedText('Match') }}</th>
                                        <th>{{ getTranslatedText('First 5 Numbers') }}</th>
                                        <th>{{ getTranslatedText('Last 5 Numbers') }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(odd, idx) in permutationGames[activeGame].odds" :key="idx">
                                        <td>{{ getTranslatedText(odd.match) }}</td>
                                        <td class="highlight">{{ odd.first5 }}x</td>
                                        <td class="highlight">{{ odd.last5 }}x</td>
                                    </tr>
                                </tbody>
                            </table>

                            <!-- Single column odds table for regular games -->
                            <table v-else class="payout-banker-permutation__table odds-table">
                                <thead>
                                    <tr>
                                        <th>{{ getTranslatedText('Match') }}</th>
                                        <th>{{ getTranslatedText('Multiplier') }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(odd, idx) in permutationGames[activeGame].odds" :key="idx">
                                        <td>{{ getTranslatedText(odd.match) }}</td>
                                        <td class="highlight">{{ odd.multiplier }}x</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- Permutation Table -->
                        <div v-if="permutationGames[activeGame].permutationTable" class="payout-banker-permutation__section">
                            <h4>{{ getTranslatedText('Permutation Table') }}</h4>
                            <table class="payout-banker-permutation__table">
                                <thead>
                                    <tr>
                                        <th>{{ getTranslatedText('Numbers Selected') }}</th>
                                        <th>{{ getTranslatedText('Combinations') }}</th>
                                        <th>{{ getTranslatedText('Cost (FCFA)') }}</th>
                                        <th>{{ getTranslatedText('Max Combos') }}</th>
                                        <th>{{ getTranslatedText('Gross Max (FCFA)') }}</th>
                                        <th>{{ getTranslatedText('Net Max (FCFA)') }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(row, idx) in permutationGames[activeGame].permutationTable" :key="idx">
                                        <td>{{ row.numbers }}</td>
                                        <td>{{ row.combos }}</td>
                                        <td>{{ formatNumber(row.cost) }}</td>
                                        <td>{{ row.maxCombos }}</td>
                                        <td class="highlight">{{ formatNumber(row.grossMax) }}</td>
                                        <td>{{ formatNumber(row.netMax) }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </transition>

                <!-- Empty State -->
                <div v-if="!activeGame" class="payout-banker-permutation__empty">
                    <p>{{ getTranslatedText('Select a game to view prize tables') }}</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import languageMixins from '@/mixins/language/language-mixins'

export default {
    mixins: [languageMixins],
    data() {
        return {
            activeGame: null,
            activeType: null, // 'banker' or 'permutation'
            bankerGames: {
                // ==================== CHANCE 2 ====================
                chance2: {
                    title: 'Banker - Chance 2',
                    odds: [
                        { match: '2 numbers', multiplier: 150 },
                        { match: '1 number + 1 bonus', multiplier: 15 },
                        { match: '0 number + 2 bonus', multiplier: 15 },
                        { match: '1 number', multiplier: 1 },
                        { match: '0 number + 1 bonus', multiplier: 1 },
                    ],
                    bankerAgainstAll: {
                        combinations: 89,
                        cost: 4450,
                        maxLines: 6,
                        grossMax: 31500,
                        netMax: 27050
                    },
                    fixBankerTable: {
                        fixCount: 1,
                        rows: [
                            { support: 2, combos: 2, cost: 100, maxLines: 2, grossMax: 15000, netMax: 14900 },
                            { support: 3, combos: 3, cost: 150, maxLines: 3, grossMax: 22500, netMax: 22350 },
                            { support: 4, combos: 4, cost: 200, maxLines: 4, grossMax: 30000, netMax: 29800 },
                            { support: 5, combos: 5, cost: 250, maxLines: 5, grossMax: 30750, netMax: 30500 },
                            { support: 6, combos: 6, cost: 300, maxLines: 6, grossMax: 31500, netMax: 31200 },
                            { support: 7, combos: 7, cost: 350, maxLines: 6, grossMax: 31500, netMax: 31150 },
                            { support: 8, combos: 8, cost: 400, maxLines: 6, grossMax: 31500, netMax: 31100 },
                            { support: 9, combos: 9, cost: 450, maxLines: 6, grossMax: 31500, netMax: 31050 },
                            { support: 10, combos: 10, cost: 500, maxLines: 6, grossMax: 31500, netMax: 31000 },
                            { support: 11, combos: 11, cost: 550, maxLines: 6, grossMax: 31500, netMax: 30950 },
                            { support: 12, combos: 12, cost: 600, maxLines: 6, grossMax: 31500, netMax: 30900 },
                            { support: 13, combos: 13, cost: 650, maxLines: 6, grossMax: 31500, netMax: 30850 },
                            { support: 14, combos: 14, cost: 700, maxLines: 6, grossMax: 31500, netMax: 30800 },
                            { support: 15, combos: 15, cost: 750, maxLines: 6, grossMax: 31500, netMax: 30750 },
                            { support: 16, combos: 16, cost: 800, maxLines: 6, grossMax: 31500, netMax: 30700 },
                            { support: 17, combos: 17, cost: 850, maxLines: 6, grossMax: 31500, netMax: 30650 },
                            { support: 18, combos: 18, cost: 900, maxLines: 6, grossMax: 31500, netMax: 30600 },
                            { support: 19, combos: 19, cost: 950, maxLines: 6, grossMax: 31500, netMax: 30550 },
                            { support: 20, combos: 20, cost: 1000, maxLines: 6, grossMax: 31500, netMax: 30500 },
                        ]
                    }
                },

                // ==================== CHANCE 3 ====================
                chance3: {
                    title: 'Banker - Chance 3',
                    odds: [
                        { match: '3 numbers', multiplier: 2000 },
                        { match: '2 numbers + 1 bonus', multiplier: 60 },
                        { match: '1 numéro + 2 bonus', multiplier: 60 },
                        { match: '2 numbers', multiplier: 40 },
                        { match: '1 numéro + 1 bonus', multiplier: 2 },
                        { match: '0 numbers + 2 bonus', multiplier: 2 },
                        { match: '1 numéro', multiplier: 1 },
                    ],
                    fixBankerTable: {
                        fixCount: 2,
                        rows: [
                            { support: 2, combos: 2, cost: 100, maxLines: 2, grossMax: 200000, netMax: 199900 },
                            { support: 3, combos: 3, cost: 150, maxLines: 3, grossMax: 300000, netMax: 299850 },
                            { support: 4, combos: 4, cost: 200, maxLines: 4, grossMax: 303000, netMax: 302800 },
                            { support: 5, combos: 5, cost: 250, maxLines: 5, grossMax: 306000, netMax: 305750 },
                            { support: 6, combos: 6, cost: 300, maxLines: 5, grossMax: 306000, netMax: 305700 },
                            { support: 7, combos: 7, cost: 350, maxLines: 5, grossMax: 306000, netMax: 305650 },
                            { support: 8, combos: 8, cost: 400, maxLines: 5, grossMax: 306000, netMax: 305600 },
                            { support: 9, combos: 9, cost: 450, maxLines: 5, grossMax: 306000, netMax: 305550 },
                            { support: 10, combos: 10, cost: 500, maxLines: 5, grossMax: 306000, netMax: 305500 },
                            { support: 11, combos: 11, cost: 550, maxLines: 5, grossMax: 306000, netMax: 305450 },
                            { support: 12, combos: 12, cost: 600, maxLines: 5, grossMax: 306000, netMax: 305400 },
                            { support: 13, combos: 13, cost: 650, maxLines: 5, grossMax: 306000, netMax: 305350 },
                            { support: 14, combos: 14, cost: 700, maxLines: 5, grossMax: 306000, netMax: 305300 },
                            { support: 15, combos: 15, cost: 750, maxLines: 5, grossMax: 306000, netMax: 305250 },
                            { support: 16, combos: 16, cost: 800, maxLines: 5, grossMax: 306000, netMax: 305200 },
                            { support: 17, combos: 17, cost: 850, maxLines: 5, grossMax: 306000, netMax: 305150 },
                            { support: 18, combos: 18, cost: 900, maxLines: 5, grossMax: 306000, netMax: 305100 },
                            { support: 19, combos: 19, cost: 950, maxLines: 5, grossMax: 306000, netMax: 305050 },
                            { support: 20, combos: 20, cost: 1000, maxLines: 5, grossMax: 306000, netMax: 305000 },
                        ]
                    }
                },

                // ==================== CHANCE 4 ====================
                chance4: {
                    title: 'Banker - Chance 4',
                    odds: [
                        { match: '4 numbers', multiplier: 10000 },
                        { match: '3 numbers + 1 bonus', multiplier: 1000 },
                        { match: '2 numbers + 2 bonus', multiplier: 1000 },
                        { match: '3 numbers', multiplier: 200 },
                        { match: '2 numbers + 1 bonus', multiplier: 40 },
                        { match: '1 numéro + 2 bonus', multiplier: 40 },
                        { match: '2 numbers', multiplier: 20 },
                        { match: '1 numéro', multiplier: 1 },
                    ],
                    fixBankerTable: {
                        fixCount: 3,
                        rows: [
                            { support: 2, combos: 2, cost: 100, maxLines: 2, grossMax: 1000000, netMax: 999900 },
                            { support: 3, combos: 3, cost: 150, maxLines: 3, grossMax: 1050000, netMax: 1049850 },
                            { support: 4, combos: 4, cost: 200, maxLines: 4, grossMax: 1100000, netMax: 1099800 },
                            { support: 5, combos: 5, cost: 250, maxLines: 4, grossMax: 1100000, netMax: 1099750 },
                            { support: 6, combos: 6, cost: 300, maxLines: 4, grossMax: 1100000, netMax: 1099700 },
                            { support: 7, combos: 7, cost: 350, maxLines: 4, grossMax: 1100000, netMax: 1099650 },
                            { support: 8, combos: 8, cost: 400, maxLines: 4, grossMax: 1100000, netMax: 1099600 },
                            { support: 9, combos: 9, cost: 450, maxLines: 4, grossMax: 1100000, netMax: 1099550 },
                            { support: 10, combos: 10, cost: 500, maxLines: 4, grossMax: 1100000, netMax: 1099500 },
                            { support: 11, combos: 11, cost: 550, maxLines: 4, grossMax: 1100000, netMax: 1099450 },
                            { support: 12, combos: 12, cost: 600, maxLines: 4, grossMax: 1100000, netMax: 1099400 },
                            { support: 13, combos: 13, cost: 650, maxLines: 4, grossMax: 1100000, netMax: 1099350 },
                            { support: 14, combos: 14, cost: 700, maxLines: 4, grossMax: 1100000, netMax: 1099300 },
                            { support: 15, combos: 15, cost: 750, maxLines: 4, grossMax: 1100000, netMax: 1099250 },
                            { support: 16, combos: 16, cost: 800, maxLines: 4, grossMax: 1100000, netMax: 1099200 },
                            { support: 17, combos: 17, cost: 850, maxLines: 4, grossMax: 1100000, netMax: 1099150 },
                            { support: 18, combos: 18, cost: 900, maxLines: 4, grossMax: 1100000, netMax: 1099100 },
                            { support: 19, combos: 19, cost: 950, maxLines: 4, grossMax: 1100000, netMax: 1099050 },
                            { support: 20, combos: 20, cost: 1000, maxLines: 4, grossMax: 1100000, netMax: 1099000 },
                        ]
                    }
                },

                // ==================== CHANCE 5 ====================
                chance5: {
                    title: 'Banker - Chance 5',
                    odds: [
                        { match: '5 numbers', multiplier: 100000 },
                        { match: '4 numbers + 1 bonus', multiplier: 10000 },
                        { match: '3 numbers + 2 bonus', multiplier: 10000 },
                        { match: '4 numbers', multiplier: 5000 },
                        { match: '3 numbers + 1 bonus', multiplier: 500 },
                        { match: '2 numbers + 2 bonus', multiplier: 500 },
                        { match: '3 numbers', multiplier: 100 },
                        { match: '2 numbers + 1 bonus', multiplier: 15 },
                        { match: '2 numbers', multiplier: 10 },
                        { match: '1 numéro', multiplier: 1 },
                    ],
                    fixBankerTable: {
                        fixCount: 4,
                        rows: [
                            { support: 2, combos: 2, cost: 100, maxLines: 2, grossMax: 5500000, netMax: 5499900 },
                            { support: 3, combos: 3, cost: 150, maxLines: 3, grossMax: 6000000, netMax: 5999850 },
                            { support: 4, combos: 4, cost: 200, maxLines: 3, grossMax: 6000000, netMax: 5999800 },
                            { support: 5, combos: 5, cost: 250, maxLines: 3, grossMax: 6000000, netMax: 5999750 },
                            { support: 6, combos: 6, cost: 300, maxLines: 3, grossMax: 6000000, netMax: 5999700 },
                            { support: 7, combos: 7, cost: 350, maxLines: 3, grossMax: 6000000, netMax: 5999650 },
                            { support: 8, combos: 8, cost: 400, maxLines: 3, grossMax: 6000000, netMax: 5999600 },
                            { support: 9, combos: 9, cost: 450, maxLines: 3, grossMax: 6000000, netMax: 5999550 },
                            { support: 10, combos: 10, cost: 500, maxLines: 3, grossMax: 6000000, netMax: 5999500 },
                            { support: 11, combos: 11, cost: 550, maxLines: 3, grossMax: 6000000, netMax: 5999450 },
                            { support: 12, combos: 12, cost: 600, maxLines: 3, grossMax: 6000000, netMax: 5999400 },
                            { support: 13, combos: 13, cost: 650, maxLines: 3, grossMax: 6000000, netMax: 5999350 },
                            { support: 14, combos: 14, cost: 700, maxLines: 3, grossMax: 6000000, netMax: 5999300 },
                            { support: 15, combos: 15, cost: 750, maxLines: 3, grossMax: 6000000, netMax: 5999250 },
                            { support: 16, combos: 16, cost: 800, maxLines: 3, grossMax: 6000000, netMax: 5999200 },
                            { support: 17, combos: 17, cost: 850, maxLines: 3, grossMax: 6000000, netMax: 5999150 },
                            { support: 18, combos: 18, cost: 900, maxLines: 3, grossMax: 6000000, netMax: 5999100 },
                            { support: 19, combos: 19, cost: 950, maxLines: 3, grossMax: 6000000, netMax: 5999050 },
                            { support: 20, combos: 20, cost: 1000, maxLines: 3, grossMax: 6000000, netMax: 5999000 },
                        ]
                    }
                },

                // ==================== CHANCE+ 3 ====================
                chancePlus3: {
                    title: 'Banker - Chance+ 3',
                    odds: [
                        { match: '3 numbers', multiplier: 3100 },
                        { match: '2 numbers + 1 bonus', multiplier: 80 },
                        { match: '1 numéro + 2 bonus', multiplier: 80 },
                        { match: '2 numbers', multiplier: 52 },
                        { match: '1 numéro + 1 bonus', multiplier: 2 },
                        { match: '0 numbers + 2 bonus', multiplier: 2 },
                    ],
                    fixBankerTable: {
                        fixCount: 2,
                        rows: [
                            { support: 2, combos: 2, cost: 100, maxLines: 2, grossMax: 310000, netMax: 309900 },
                            { support: 3, combos: 3, cost: 150, maxLines: 3, grossMax: 465000, netMax: 464850 },
                            { support: 4, combos: 4, cost: 200, maxLines: 4, grossMax: 469000, netMax: 468800 },
                            { support: 5, combos: 5, cost: 250, maxLines: 5, grossMax: 473000, netMax: 472750 },
                            { support: 6, combos: 6, cost: 300, maxLines: 5, grossMax: 473000, netMax: 472700 },
                            { support: 7, combos: 7, cost: 350, maxLines: 5, grossMax: 473000, netMax: 472650 },
                            { support: 8, combos: 8, cost: 400, maxLines: 5, grossMax: 473000, netMax: 472600 },
                            { support: 9, combos: 9, cost: 450, maxLines: 5, grossMax: 473000, netMax: 472550 },
                            { support: 10, combos: 10, cost: 500, maxLines: 5, grossMax: 473000, netMax: 472500 },
                            { support: 11, combos: 11, cost: 550, maxLines: 5, grossMax: 473000, netMax: 472450 },
                            { support: 12, combos: 12, cost: 600, maxLines: 5, grossMax: 473000, netMax: 472400 },
                            { support: 13, combos: 13, cost: 650, maxLines: 5, grossMax: 473000, netMax: 472350 },
                            { support: 14, combos: 14, cost: 700, maxLines: 5, grossMax: 473000, netMax: 472300 },
                            { support: 15, combos: 15, cost: 750, maxLines: 5, grossMax: 473000, netMax: 472250 },
                            { support: 16, combos: 16, cost: 800, maxLines: 5, grossMax: 473000, netMax: 472200 },
                            { support: 17, combos: 17, cost: 850, maxLines: 5, grossMax: 473000, netMax: 472150 },
                            { support: 18, combos: 18, cost: 900, maxLines: 5, grossMax: 473000, netMax: 472100 },
                            { support: 19, combos: 19, cost: 950, maxLines: 5, grossMax: 473000, netMax: 472050 },
                            { support: 20, combos: 20, cost: 1000, maxLines: 5, grossMax: 473000, netMax: 472000 },
                        ]
                    }
                },

                // ==================== CHANCE+ 4 ====================
                chancePlus4: {
                    title: 'Banker - Chance+ 4',
                    odds: [
                        { match: '4 numbers', multiplier: 30000 },
                        { match: '3 numbers + 1 bonus', multiplier: 2000 },
                        { match: '2 numbers + 2 bonus', multiplier: 2000 },
                        { match: '3 numbers', multiplier: 300 },
                        { match: '2 numbers + 1 bonus', multiplier: 35 },
                        { match: '1 numéro + 2 bonus', multiplier: 35 },
                        { match: '2 numbers', multiplier: 30 },
                        { match: '1 numéro + 1 bonus', multiplier: 3 },
                        { match: '0 numbers + 2 bonus', multiplier: 3 },
                    ],
                    fixBankerTable: {
                        fixCount: 3,
                        rows: [
                            { support: 2, combos: 2, cost: 100, maxLines: 2, grossMax: 3000000, netMax: 2999900 },
                            { support: 3, combos: 3, cost: 150, maxLines: 3, grossMax: 3150000, netMax: 3149850 },
                            { support: 4, combos: 4, cost: 200, maxLines: 4, grossMax: 3300000, netMax: 3299800 },
                            { support: 5, combos: 5, cost: 250, maxLines: 4, grossMax: 3300000, netMax: 3299750 },
                            { support: 6, combos: 6, cost: 300, maxLines: 4, grossMax: 3300000, netMax: 3299700 },
                            { support: 7, combos: 7, cost: 350, maxLines: 4, grossMax: 3300000, netMax: 3299650 },
                            { support: 8, combos: 8, cost: 400, maxLines: 4, grossMax: 3300000, netMax: 3299600 },
                            { support: 9, combos: 9, cost: 450, maxLines: 4, grossMax: 3300000, netMax: 3299550 },
                            { support: 10, combos: 10, cost: 500, maxLines: 4, grossMax: 3300000, netMax: 3299500 },
                            { support: 11, combos: 11, cost: 550, maxLines: 4, grossMax: 3300000, netMax: 3299450 },
                            { support: 12, combos: 12, cost: 600, maxLines: 4, grossMax: 3300000, netMax: 3299400 },
                            { support: 13, combos: 13, cost: 650, maxLines: 4, grossMax: 3300000, netMax: 3299350 },
                            { support: 14, combos: 14, cost: 700, maxLines: 4, grossMax: 3300000, netMax: 3299300 },
                            { support: 15, combos: 15, cost: 750, maxLines: 4, grossMax: 3300000, netMax: 3299250 },
                            { support: 16, combos: 16, cost: 800, maxLines: 4, grossMax: 3300000, netMax: 3299200 },
                            { support: 17, combos: 17, cost: 850, maxLines: 4, grossMax: 3300000, netMax: 3299150 },
                            { support: 18, combos: 18, cost: 900, maxLines: 4, grossMax: 3300000, netMax: 3299100 },
                            { support: 19, combos: 19, cost: 950, maxLines: 4, grossMax: 3300000, netMax: 3299050 },
                            { support: 20, combos: 20, cost: 1000, maxLines: 4, grossMax: 3300000, netMax: 3299000 },
                        ]
                    }
                },

                // ==================== CHANCE+ 5 ====================
                chancePlus5: {
                    title: 'Banker - Chance+ 5',
                    odds: [
                        { match: '5 numbers', multiplier: 200000 },
                        { match: '4 numbers + 1 bonus', multiplier: 20000 },
                        { match: '3 numbers + 2 bonus', multiplier: 20000 },
                        { match: '4 numbers', multiplier: 10000 },
                        { match: '3 numbers + 1 bonus', multiplier: 500 },
                        { match: '2 numbers + 2 bonus', multiplier: 500 },
                        { match: '3 numbers', multiplier: 200 },
                        { match: '2 numbers + 1 bonus', multiplier: 20 },
                        { match: '1 numéro + 2 bonus', multiplier: 20 },
                        { match: '2 numbers', multiplier: 15 },
                    ],
                    fixBankerTable: {
                        fixCount: 4,
                        rows: [
                            { support: 2, combos: 2, cost: 100, maxLines: 2, grossMax: 11000000, netMax: 10999900 },
                            { support: 3, combos: 3, cost: 150, maxLines: 3, grossMax: 12000000, netMax: 11999850 },
                            { support: 4, combos: 4, cost: 200, maxLines: 3, grossMax: 12000000, netMax: 11999800 },
                            { support: 5, combos: 5, cost: 250, maxLines: 3, grossMax: 12000000, netMax: 11999750 },
                            { support: 6, combos: 6, cost: 300, maxLines: 3, grossMax: 12000000, netMax: 11999700 },
                            { support: 7, combos: 7, cost: 350, maxLines: 3, grossMax: 12000000, netMax: 11999650 },
                            { support: 8, combos: 8, cost: 400, maxLines: 3, grossMax: 12000000, netMax: 11999600 },
                            { support: 9, combos: 9, cost: 450, maxLines: 3, grossMax: 12000000, netMax: 11999550 },
                            { support: 10, combos: 10, cost: 500, maxLines: 3, grossMax: 12000000, netMax: 11999500 },
                            { support: 11, combos: 11, cost: 550, maxLines: 3, grossMax: 12000000, netMax: 11999450 },
                            { support: 12, combos: 12, cost: 600, maxLines: 3, grossMax: 12000000, netMax: 11999400 },
                            { support: 13, combos: 13, cost: 650, maxLines: 3, grossMax: 12000000, netMax: 11999350 },
                            { support: 14, combos: 14, cost: 700, maxLines: 3, grossMax: 12000000, netMax: 11999300 },
                            { support: 15, combos: 15, cost: 750, maxLines: 3, grossMax: 12000000, netMax: 11999250 },
                            { support: 16, combos: 16, cost: 800, maxLines: 3, grossMax: 12000000, netMax: 11999200 },
                            { support: 17, combos: 17, cost: 850, maxLines: 3, grossMax: 12000000, netMax: 11999150 },
                            { support: 18, combos: 18, cost: 900, maxLines: 3, grossMax: 12000000, netMax: 11999100 },
                            { support: 19, combos: 19, cost: 950, maxLines: 3, grossMax: 12000000, netMax: 11999050 },
                            { support: 20, combos: 20, cost: 1000, maxLines: 3, grossMax: 12000000, netMax: 11999000 },
                        ]
                    }
                },

                // ==================== DOUBLE CHANCE+ 3 ====================
                doubleChance3: {
                    title: 'Banker - Double Chance+ 3',
                    hasDualOdds: true,
                    odds: [
                        { match: '3 numbers', first5: '1,850', last5: '1,250' },
                        { match: '2 numbers', first5: '31', last5: '21' },
                    ],
                    fixBankerTable: {
                        fixCount: 2,
                        rows: [
                            { support: 2, combos: 2, cost: 100, maxLines: 2, grossMax: 185000, netMax: 184900 },
                            { support: 3, combos: 3, cost: 150, maxLines: 3, grossMax: 277500, netMax: 277350 },
                            { support: 4, combos: 4, cost: 200, maxLines: 3, grossMax: 277500, netMax: 277300 },
                            { support: 5, combos: 5, cost: 250, maxLines: 3, grossMax: 277500, netMax: 277250 },
                            { support: 6, combos: 6, cost: 300, maxLines: 3, grossMax: 277500, netMax: 277200 },
                            { support: 7, combos: 7, cost: 350, maxLines: 3, grossMax: 277500, netMax: 277150 },
                            { support: 8, combos: 8, cost: 400, maxLines: 3, grossMax: 277500, netMax: 277100 },
                            { support: 9, combos: 9, cost: 450, maxLines: 3, grossMax: 277500, netMax: 277050 },
                            { support: 10, combos: 10, cost: 500, maxLines: 3, grossMax: 277500, netMax: 277000 },
                            { support: 11, combos: 11, cost: 550, maxLines: 3, grossMax: 277500, netMax: 276950 },
                            { support: 12, combos: 12, cost: 600, maxLines: 3, grossMax: 277500, netMax: 276900 },
                            { support: 13, combos: 13, cost: 650, maxLines: 3, grossMax: 277500, netMax: 276850 },
                            { support: 14, combos: 14, cost: 700, maxLines: 3, grossMax: 277500, netMax: 276800 },
                            { support: 15, combos: 15, cost: 750, maxLines: 3, grossMax: 277500, netMax: 276750 },
                            { support: 16, combos: 16, cost: 800, maxLines: 3, grossMax: 277500, netMax: 276700 },
                            { support: 17, combos: 17, cost: 850, maxLines: 3, grossMax: 277500, netMax: 276650 },
                            { support: 18, combos: 18, cost: 900, maxLines: 3, grossMax: 277500, netMax: 276600 },
                            { support: 19, combos: 19, cost: 950, maxLines: 3, grossMax: 277500, netMax: 276550 },
                            { support: 20, combos: 20, cost: 1000, maxLines: 3, grossMax: 277500, netMax: 276500 },
                        ]
                    }
                },

                // ==================== DOUBLE CHANCE+ 4 ====================
                doubleChance4: {
                    title: 'Banker - Double Chance+ 4',
                    hasDualOdds: true,
                    odds: [
                        { match: '4 numbers', first5: '18,000', last5: '12,000' },
                        { match: '3 numbers', first5: '180', last5: '120' },
                        { match: '2 numbers', first5: '18', last5: '12' },
                    ],
                    fixBankerTable: {
                        fixCount: 3,
                        rows: [
                            { support: 2, combos: 2, cost: 100, maxLines: 2, grossMax: 1800000, netMax: 1799900 },
                            { support: 3, combos: 3, cost: 150, maxLines: 2, grossMax: 1800000, netMax: 1799850 },
                            { support: 4, combos: 4, cost: 200, maxLines: 2, grossMax: 1800000, netMax: 1799800 },
                            { support: 5, combos: 5, cost: 250, maxLines: 2, grossMax: 1800000, netMax: 1799750 },
                            { support: 6, combos: 6, cost: 300, maxLines: 2, grossMax: 1800000, netMax: 1799700 },
                            { support: 7, combos: 7, cost: 350, maxLines: 2, grossMax: 1800000, netMax: 1799650 },
                            { support: 8, combos: 8, cost: 400, maxLines: 2, grossMax: 1800000, netMax: 1799600 },
                            { support: 9, combos: 9, cost: 450, maxLines: 2, grossMax: 1800000, netMax: 1799550 },
                            { support: 10, combos: 10, cost: 500, maxLines: 2, grossMax: 1800000, netMax: 1799500 },
                            { support: 11, combos: 11, cost: 550, maxLines: 2, grossMax: 1800000, netMax: 1799450 },
                            { support: 12, combos: 12, cost: 600, maxLines: 2, grossMax: 1800000, netMax: 1799400 },
                            { support: 13, combos: 13, cost: 650, maxLines: 2, grossMax: 1800000, netMax: 1799350 },
                            { support: 14, combos: 14, cost: 700, maxLines: 2, grossMax: 1800000, netMax: 1799300 },
                            { support: 15, combos: 15, cost: 750, maxLines: 2, grossMax: 1800000, netMax: 1799250 },
                            { support: 16, combos: 16, cost: 800, maxLines: 2, grossMax: 1800000, netMax: 1799200 },
                            { support: 17, combos: 17, cost: 850, maxLines: 2, grossMax: 1800000, netMax: 1799150 },
                            { support: 18, combos: 18, cost: 900, maxLines: 2, grossMax: 1800000, netMax: 1799100 },
                            { support: 19, combos: 19, cost: 950, maxLines: 2, grossMax: 1800000, netMax: 1799050 },
                            { support: 20, combos: 20, cost: 1000, maxLines: 2, grossMax: 1800000, netMax: 1799000 },
                        ]
                    }
                },

                // ==================== DOUBLE CHANCE+ 5 ====================
                doubleChance5: {
                    title: 'Banker - Double Chance+ 5',
                    hasDualOdds: true,
                    odds: [
                        { match: '5 numbers', first5: '120,000', last5: '80,000' },
                        { match: '4 numbers', first5: '6,000', last5: '4,000' },
                        { match: '3 numbers', first5: '120', last5: '80' },
                        { match: '2 numbers', first5: '9', last5: '6' },
                    ],
                    fixBankerTable: {
                        fixCount: 4,
                        rows: [
                            { support: 2, combos: 2, cost: 100, maxLines: 1, grossMax: 6000000, netMax: 5999900 },
                            { support: 3, combos: 3, cost: 150, maxLines: 1, grossMax: 6000000, netMax: 5999850 },
                            { support: 4, combos: 4, cost: 200, maxLines: 1, grossMax: 6000000, netMax: 5999800 },
                            { support: 5, combos: 5, cost: 250, maxLines: 1, grossMax: 6000000, netMax: 5999750 },
                            { support: 6, combos: 6, cost: 300, maxLines: 1, grossMax: 6000000, netMax: 5999700 },
                            { support: 7, combos: 7, cost: 350, maxLines: 1, grossMax: 6000000, netMax: 5999650 },
                            { support: 8, combos: 8, cost: 400, maxLines: 1, grossMax: 6000000, netMax: 5999600 },
                            { support: 9, combos: 9, cost: 450, maxLines: 1, grossMax: 6000000, netMax: 5999550 },
                            { support: 10, combos: 10, cost: 500, maxLines: 1, grossMax: 6000000, netMax: 5999500 },
                            { support: 11, combos: 11, cost: 550, maxLines: 1, grossMax: 6000000, netMax: 5999450 },
                            { support: 12, combos: 12, cost: 600, maxLines: 1, grossMax: 6000000, netMax: 5999400 },
                            { support: 13, combos: 13, cost: 650, maxLines: 1, grossMax: 6000000, netMax: 5999350 },
                            { support: 14, combos: 14, cost: 700, maxLines: 1, grossMax: 6000000, netMax: 5999300 },
                            { support: 15, combos: 15, cost: 750, maxLines: 1, grossMax: 6000000, netMax: 5999250 },
                            { support: 16, combos: 16, cost: 800, maxLines: 1, grossMax: 6000000, netMax: 5999200 },
                            { support: 17, combos: 17, cost: 850, maxLines: 1, grossMax: 6000000, netMax: 5999150 },
                            { support: 18, combos: 18, cost: 900, maxLines: 1, grossMax: 6000000, netMax: 5999100 },
                            { support: 19, combos: 19, cost: 950, maxLines: 1, grossMax: 6000000, netMax: 5999050 },
                            { support: 20, combos: 20, cost: 1000, maxLines: 1, grossMax: 6000000, netMax: 5999000 },
                        ]
                    }
                },

                // ==================== TURBO 2 ====================
                turbo2: {
                    title: 'Banker - Turbo 2',
                    odds: [
                        { match: '2 numbers', multiplier: 2400 },
                        { match: '1 numéro + 1 bonus', multiplier: 40 },
                        { match: '0 numbers + 2 bonus', multiplier: 40 },
                    ],
                    fixBankerTable: {
                        fixCount: 1,
                        rows: [
                            { support: 2, combos: 2, cost: 100, maxLines: 2, grossMax: 122000, netMax: 121900 },
                            { support: 3, combos: 3, cost: 150, maxLines: 3, grossMax: 124000, netMax: 123850 },
                            { support: 4, combos: 4, cost: 200, maxLines: 3, grossMax: 124000, netMax: 123800 },
                            { support: 5, combos: 5, cost: 250, maxLines: 3, grossMax: 124000, netMax: 123750 },
                            { support: 6, combos: 6, cost: 300, maxLines: 3, grossMax: 124000, netMax: 123700 },
                            { support: 7, combos: 7, cost: 350, maxLines: 3, grossMax: 124000, netMax: 123650 },
                            { support: 8, combos: 8, cost: 400, maxLines: 3, grossMax: 124000, netMax: 123600 },
                            { support: 9, combos: 9, cost: 450, maxLines: 3, grossMax: 124000, netMax: 123550 },
                            { support: 10, combos: 10, cost: 500, maxLines: 3, grossMax: 124000, netMax: 123500 },
                            { support: 11, combos: 11, cost: 550, maxLines: 3, grossMax: 124000, netMax: 123450 },
                            { support: 12, combos: 12, cost: 600, maxLines: 3, grossMax: 124000, netMax: 123400 },
                            { support: 13, combos: 13, cost: 650, maxLines: 3, grossMax: 124000, netMax: 123350 },
                            { support: 14, combos: 14, cost: 700, maxLines: 3, grossMax: 124000, netMax: 123300 },
                            { support: 15, combos: 15, cost: 750, maxLines: 3, grossMax: 124000, netMax: 123250 },
                            { support: 16, combos: 16, cost: 800, maxLines: 3, grossMax: 124000, netMax: 123200 },
                            { support: 17, combos: 17, cost: 850, maxLines: 3, grossMax: 124000, netMax: 123150 },
                            { support: 18, combos: 18, cost: 900, maxLines: 3, grossMax: 124000, netMax: 123100 },
                            { support: 19, combos: 19, cost: 950, maxLines: 3, grossMax: 124000, netMax: 123050 },
                            { support: 20, combos: 20, cost: 1000, maxLines: 3, grossMax: 124000, netMax: 123000 },
                        ]
                    }
                },

                // ==================== TURBO 3 ====================
                turbo3: {
                    title: 'Banker - Turbo 3',
                    odds: [
                        { match: '2 numbers', multiplier: 800 },
                        { match: '1 numéro + 1 bonus', multiplier: 30 },
                        { match: '0 numbers + 2 bonus', multiplier: 30 },
                    ],
                    fixBankerTable: {
                        fixCount: 1,
                        rows: [
                            { support: 2, combos: 2, cost: 100, maxLines: 2, grossMax: 80000, netMax: 79900 },
                            { support: 3, combos: 3, cost: 150, maxLines: 3, grossMax: 81500, netMax: 81350 },
                            { support: 4, combos: 4, cost: 200, maxLines: 4, grossMax: 83000, netMax: 82800 },
                            { support: 5, combos: 5, cost: 250, maxLines: 4, grossMax: 83000, netMax: 82750 },
                            { support: 6, combos: 6, cost: 300, maxLines: 4, grossMax: 83000, netMax: 82700 },
                            { support: 7, combos: 7, cost: 350, maxLines: 4, grossMax: 83000, netMax: 82650 },
                            { support: 8, combos: 8, cost: 400, maxLines: 4, grossMax: 83000, netMax: 82600 },
                            { support: 9, combos: 9, cost: 450, maxLines: 4, grossMax: 83000, netMax: 82550 },
                            { support: 10, combos: 10, cost: 500, maxLines: 4, grossMax: 83000, netMax: 82500 },
                            { support: 11, combos: 11, cost: 550, maxLines: 4, grossMax: 83000, netMax: 82450 },
                            { support: 12, combos: 12, cost: 600, maxLines: 4, grossMax: 83000, netMax: 82400 },
                            { support: 13, combos: 13, cost: 650, maxLines: 4, grossMax: 83000, netMax: 82350 },
                            { support: 14, combos: 14, cost: 700, maxLines: 4, grossMax: 83000, netMax: 82300 },
                            { support: 15, combos: 15, cost: 750, maxLines: 4, grossMax: 83000, netMax: 82250 },
                            { support: 16, combos: 16, cost: 800, maxLines: 4, grossMax: 83000, netMax: 82200 },
                            { support: 17, combos: 17, cost: 850, maxLines: 4, grossMax: 83000, netMax: 82150 },
                            { support: 18, combos: 18, cost: 900, maxLines: 4, grossMax: 83000, netMax: 82100 },
                            { support: 19, combos: 19, cost: 950, maxLines: 4, grossMax: 83000, netMax: 82050 },
                            { support: 20, combos: 20, cost: 1000, maxLines: 4, grossMax: 83000, netMax: 82000 },
                        ]
                    }
                },

                // ==================== TURBO 4 ====================
                turbo4: {
                    title: 'Banker - Turbo 4',
                    odds: [
                        { match: '2 numbers', multiplier: 400 },
                        { match: '1 numéro + 1 bonus', multiplier: 25 },
                        { match: '0 numbers + 2 bonus', multiplier: 25 },
                    ],
                    fixBankerTable: {
                        fixCount: 1,
                        rows: [
                            { support: 2, combos: 2, cost: 100, maxLines: 2, grossMax: 40000, netMax: 39900 },
                            { support: 3, combos: 3, cost: 150, maxLines: 3, grossMax: 60000, netMax: 59850 },
                            { support: 4, combos: 4, cost: 200, maxLines: 4, grossMax: 61250, netMax: 61050 },
                            { support: 5, combos: 5, cost: 250, maxLines: 5, grossMax: 62500, netMax: 62250 },
                            { support: 6, combos: 6, cost: 300, maxLines: 5, grossMax: 62500, netMax: 62200 },
                            { support: 7, combos: 7, cost: 350, maxLines: 5, grossMax: 62500, netMax: 62150 },
                            { support: 8, combos: 8, cost: 400, maxLines: 5, grossMax: 62500, netMax: 62100 },
                            { support: 9, combos: 9, cost: 450, maxLines: 5, grossMax: 62500, netMax: 62050 },
                            { support: 10, combos: 10, cost: 500, maxLines: 5, grossMax: 62500, netMax: 62000 },
                            { support: 11, combos: 11, cost: 550, maxLines: 5, grossMax: 62500, netMax: 61950 },
                            { support: 12, combos: 12, cost: 600, maxLines: 5, grossMax: 62500, netMax: 61900 },
                            { support: 13, combos: 13, cost: 650, maxLines: 5, grossMax: 62500, netMax: 61850 },
                            { support: 14, combos: 14, cost: 700, maxLines: 5, grossMax: 62500, netMax: 61800 },
                            { support: 15, combos: 15, cost: 750, maxLines: 5, grossMax: 62500, netMax: 61750 },
                            { support: 16, combos: 16, cost: 800, maxLines: 5, grossMax: 62500, netMax: 61700 },
                            { support: 17, combos: 17, cost: 850, maxLines: 5, grossMax: 62500, netMax: 61650 },
                            { support: 18, combos: 18, cost: 900, maxLines: 5, grossMax: 62500, netMax: 61600 },
                            { support: 19, combos: 19, cost: 950, maxLines: 5, grossMax: 62500, netMax: 61550 },
                            { support: 20, combos: 20, cost: 1000, maxLines: 5, grossMax: 62500, netMax: 61500 },
                        ]
                    }
                },

                // ==================== TURBO 5 ====================
                turbo5: {
                    title: 'Banker - Turbo 5',
                    odds: [
                        { match: '2 numbers', multiplier: 300 },
                        { match: '1 numéro + 1 bonus', multiplier: 12 },
                        { match: '0 numbers + 2 bonus', multiplier: 12 },
                    ],
                    fixBankerTable: {
                        fixCount: 1,
                        rows: [
                            { support: 2, combos: 2, cost: 100, maxLines: 2, grossMax: 30000, netMax: 29900 },
                            { support: 3, combos: 3, cost: 150, maxLines: 3, grossMax: 45000, netMax: 44850 },
                            { support: 4, combos: 4, cost: 200, maxLines: 4, grossMax: 60000, netMax: 59800 },
                            { support: 5, combos: 5, cost: 250, maxLines: 5, grossMax: 60600, netMax: 60350 },
                            { support: 6, combos: 6, cost: 300, maxLines: 5, grossMax: 61200, netMax: 60900 },
                            { support: 7, combos: 7, cost: 350, maxLines: 6, grossMax: 61200, netMax: 60850 },
                            { support: 8, combos: 8, cost: 400, maxLines: 6, grossMax: 61200, netMax: 60800 },
                            { support: 9, combos: 9, cost: 450, maxLines: 6, grossMax: 61200, netMax: 60750 },
                            { support: 10, combos: 10, cost: 500, maxLines: 6, grossMax: 61200, netMax: 60700 },
                            { support: 11, combos: 11, cost: 550, maxLines: 6, grossMax: 61200, netMax: 60650 },
                            { support: 12, combos: 12, cost: 600, maxLines: 6, grossMax: 61200, netMax: 60600 },
                            { support: 13, combos: 13, cost: 650, maxLines: 6, grossMax: 61200, netMax: 60550 },
                            { support: 14, combos: 14, cost: 700, maxLines: 6, grossMax: 61200, netMax: 60500 },
                            { support: 15, combos: 15, cost: 750, maxLines: 6, grossMax: 61200, netMax: 60450 },
                            { support: 16, combos: 16, cost: 800, maxLines: 6, grossMax: 61200, netMax: 60400 },
                            { support: 17, combos: 17, cost: 850, maxLines: 6, grossMax: 61200, netMax: 60350 },
                            { support: 18, combos: 18, cost: 900, maxLines: 6, grossMax: 61200, netMax: 60300 },
                            { support: 19, combos: 19, cost: 950, maxLines: 6, grossMax: 61200, netMax: 60250 },
                            { support: 20, combos: 20, cost: 1000, maxLines: 6, grossMax: 61200, netMax: 60200 },
                        ]
                    }
                },

                // ==================== DC TURBO 2 ====================
                dcTurbo2: {
                    title: 'Banker - DC Turbo 2',
                    hasDualOdds: true,
                    odds: [
                        { match: '2 numbers', first5: '1,500', last5: '1,100' },
                    ],
                    fixBankerTable: {
                        fixCount: 1,
                        rows: [
                            { support: 2, combos: 2, cost: 100, maxLines: 1, grossMax: 75000, netMax: 74900 },
                            { support: 3, combos: 3, cost: 150, maxLines: 1, grossMax: 75000, netMax: 74850 },
                            { support: 4, combos: 4, cost: 200, maxLines: 1, grossMax: 75000, netMax: 74800 },
                            { support: 5, combos: 5, cost: 250, maxLines: 1, grossMax: 75000, netMax: 74750 },
                            { support: 6, combos: 6, cost: 300, maxLines: 1, grossMax: 75000, netMax: 74700 },
                            { support: 7, combos: 7, cost: 350, maxLines: 1, grossMax: 75000, netMax: 74650 },
                            { support: 8, combos: 8, cost: 400, maxLines: 1, grossMax: 75000, netMax: 74600 },
                            { support: 9, combos: 9, cost: 450, maxLines: 1, grossMax: 75000, netMax: 74550 },
                            { support: 10, combos: 10, cost: 500, maxLines: 1, grossMax: 75000, netMax: 74500 },
                            { support: 11, combos: 11, cost: 550, maxLines: 1, grossMax: 75000, netMax: 74450 },
                            { support: 12, combos: 12, cost: 600, maxLines: 1, grossMax: 75000, netMax: 74400 },
                            { support: 13, combos: 13, cost: 650, maxLines: 1, grossMax: 75000, netMax: 74350 },
                            { support: 14, combos: 14, cost: 700, maxLines: 1, grossMax: 75000, netMax: 74300 },
                            { support: 15, combos: 15, cost: 750, maxLines: 1, grossMax: 75000, netMax: 74250 },
                            { support: 16, combos: 16, cost: 800, maxLines: 1, grossMax: 75000, netMax: 74200 },
                            { support: 17, combos: 17, cost: 850, maxLines: 1, grossMax: 75000, netMax: 74150 },
                            { support: 18, combos: 18, cost: 900, maxLines: 1, grossMax: 75000, netMax: 74100 },
                            { support: 19, combos: 19, cost: 950, maxLines: 1, grossMax: 75000, netMax: 74050 },
                            { support: 20, combos: 20, cost: 1000, maxLines: 1, grossMax: 75000, netMax: 74000 },
                        ]
                    }
                },

                // ==================== DC TURBO 3 ====================
                dcTurbo3: {
                    title: 'Banker - DC Turbo 3',
                    hasDualOdds: true,
                    odds: [
                        { match: '2 numbers', first5: '520', last5: '320' },
                    ],
                    fixBankerTable: {
                        fixCount: 1,
                        rows: [
                            { support: 2, combos: 2, cost: 100, maxLines: 2, grossMax: 52000, netMax: 51900 },
                            { support: 3, combos: 3, cost: 150, maxLines: 2, grossMax: 52000, netMax: 51850 },
                            { support: 4, combos: 4, cost: 200, maxLines: 2, grossMax: 52000, netMax: 51800 },
                            { support: 5, combos: 5, cost: 250, maxLines: 2, grossMax: 52000, netMax: 51750 },
                            { support: 6, combos: 6, cost: 300, maxLines: 2, grossMax: 52000, netMax: 51700 },
                            { support: 7, combos: 7, cost: 350, maxLines: 2, grossMax: 52000, netMax: 51650 },
                            { support: 8, combos: 8, cost: 400, maxLines: 2, grossMax: 52000, netMax: 51600 },
                            { support: 9, combos: 9, cost: 450, maxLines: 2, grossMax: 52000, netMax: 51550 },
                            { support: 10, combos: 10, cost: 500, maxLines: 2, grossMax: 52000, netMax: 51500 },
                            { support: 11, combos: 11, cost: 550, maxLines: 2, grossMax: 52000, netMax: 51450 },
                            { support: 12, combos: 12, cost: 600, maxLines: 2, grossMax: 52000, netMax: 51400 },
                            { support: 13, combos: 13, cost: 650, maxLines: 2, grossMax: 52000, netMax: 51350 },
                            { support: 14, combos: 14, cost: 700, maxLines: 2, grossMax: 52000, netMax: 51300 },
                            { support: 15, combos: 15, cost: 750, maxLines: 2, grossMax: 52000, netMax: 51250 },
                            { support: 16, combos: 16, cost: 800, maxLines: 2, grossMax: 52000, netMax: 51200 },
                            { support: 17, combos: 17, cost: 850, maxLines: 2, grossMax: 52000, netMax: 51150 },
                            { support: 18, combos: 18, cost: 900, maxLines: 2, grossMax: 52000, netMax: 51100 },
                            { support: 19, combos: 19, cost: 950, maxLines: 2, grossMax: 52000, netMax: 51050 },
                            { support: 20, combos: 20, cost: 1000, maxLines: 2, grossMax: 52000, netMax: 51000 },
                        ]
                    }
                },

                // ==================== DC TURBO 4 ====================
                dcTurbo4: {
                    title: 'Banker - DC Turbo 4',
                    hasDualOdds: true,
                    odds: [
                        { match: '2 numbers', first5: '260', last5: '160' },
                    ],
                    fixBankerTable: {
                        fixCount: 1,
                        rows: [
                            { support: 2, combos: 2, cost: 100, maxLines: 2, grossMax: 26000, netMax: 25900 },
                            { support: 3, combos: 3, cost: 150, maxLines: 3, grossMax: 39000, netMax: 38850 },
                            { support: 4, combos: 4, cost: 200, maxLines: 3, grossMax: 39000, netMax: 38800 },
                            { support: 5, combos: 5, cost: 250, maxLines: 3, grossMax: 39000, netMax: 38750 },
                            { support: 6, combos: 6, cost: 300, maxLines: 3, grossMax: 39000, netMax: 38700 },
                            { support: 7, combos: 7, cost: 350, maxLines: 3, grossMax: 39000, netMax: 38650 },
                            { support: 8, combos: 8, cost: 400, maxLines: 3, grossMax: 39000, netMax: 38600 },
                            { support: 9, combos: 9, cost: 450, maxLines: 3, grossMax: 39000, netMax: 38550 },
                            { support: 10, combos: 10, cost: 500, maxLines: 3, grossMax: 39000, netMax: 38500 },
                            { support: 11, combos: 11, cost: 550, maxLines: 3, grossMax: 39000, netMax: 38450 },
                            { support: 12, combos: 12, cost: 600, maxLines: 3, grossMax: 39000, netMax: 38400 },
                            { support: 13, combos: 13, cost: 650, maxLines: 3, grossMax: 39000, netMax: 38350 },
                            { support: 14, combos: 14, cost: 700, maxLines: 3, grossMax: 39000, netMax: 38300 },
                            { support: 15, combos: 15, cost: 750, maxLines: 3, grossMax: 39000, netMax: 38250 },
                            { support: 16, combos: 16, cost: 800, maxLines: 3, grossMax: 39000, netMax: 38200 },
                            { support: 17, combos: 17, cost: 850, maxLines: 3, grossMax: 39000, netMax: 38150 },
                            { support: 18, combos: 18, cost: 900, maxLines: 3, grossMax: 39000, netMax: 38100 },
                            { support: 19, combos: 19, cost: 950, maxLines: 3, grossMax: 39000, netMax: 38050 },
                            { support: 20, combos: 20, cost: 1000, maxLines: 3, grossMax: 39000, netMax: 38000 },
                        ]
                    }
                },

                // ==================== DC TURBO 5 ====================
                dcTurbo5: {
                    title: 'Banker - DC Turbo 5',
                    hasDualOdds: true,
                    odds: [
                        { match: '2 numbers', first5: '185', last5: '125' },
                    ],
                    fixBankerTable: {
                        fixCount: 1,
                        rows: [
                            { support: 2, combos: 2, cost: 100, maxLines: 2, grossMax: 18500, netMax: 18400 },
                            { support: 3, combos: 3, cost: 150, maxLines: 3, grossMax: 27750, netMax: 27600 },
                            { support: 4, combos: 4, cost: 200, maxLines: 4, grossMax: 37000, netMax: 36800 },
                            { support: 5, combos: 5, cost: 250, maxLines: 4, grossMax: 37000, netMax: 36750 },
                            { support: 6, combos: 6, cost: 300, maxLines: 4, grossMax: 37000, netMax: 36700 },
                            { support: 7, combos: 7, cost: 350, maxLines: 4, grossMax: 37000, netMax: 36650 },
                            { support: 8, combos: 8, cost: 400, maxLines: 4, grossMax: 37000, netMax: 36600 },
                            { support: 9, combos: 9, cost: 450, maxLines: 4, grossMax: 37000, netMax: 36550 },
                            { support: 10, combos: 10, cost: 500, maxLines: 4, grossMax: 37000, netMax: 36500 },
                            { support: 11, combos: 11, cost: 550, maxLines: 4, grossMax: 37000, netMax: 36450 },
                            { support: 12, combos: 12, cost: 600, maxLines: 4, grossMax: 37000, netMax: 36400 },
                            { support: 13, combos: 13, cost: 650, maxLines: 4, grossMax: 37000, netMax: 36350 },
                            { support: 14, combos: 14, cost: 700, maxLines: 4, grossMax: 37000, netMax: 36300 },
                            { support: 15, combos: 15, cost: 750, maxLines: 4, grossMax: 37000, netMax: 36250 },
                            { support: 16, combos: 16, cost: 800, maxLines: 4, grossMax: 37000, netMax: 36200 },
                            { support: 17, combos: 17, cost: 850, maxLines: 4, grossMax: 37000, netMax: 36150 },
                            { support: 18, combos: 18, cost: 900, maxLines: 4, grossMax: 37000, netMax: 36100 },
                            { support: 19, combos: 19, cost: 950, maxLines: 4, grossMax: 37000, netMax: 36050 },
                            { support: 20, combos: 20, cost: 1000, maxLines: 4, grossMax: 37000, netMax: 36000 },
                        ]
                    }
                },

                // ==================== EXTRA 6-2 ====================
                extra62: {
                    title: 'Banker - Extra 6-2',
                    odds: [
                        { match: '2 numbers', multiplier: 165 },
                    ],
                    fixBankerTable: {
                        fixCount: 1,
                        rows: [
                            { support: 2, combos: 2, cost: 100, maxLines: 2, grossMax: 16500, netMax: 16400 },
                            { support: 3, combos: 3, cost: 150, maxLines: 3, grossMax: 24750, netMax: 24600 },
                            { support: 4, combos: 4, cost: 200, maxLines: 4, grossMax: 33000, netMax: 32800 },
                            { support: 5, combos: 5, cost: 250, maxLines: 5, grossMax: 41250, netMax: 41000 },
                            { support: 6, combos: 6, cost: 300, maxLines: 5, grossMax: 41250, netMax: 40950 },
                            { support: 7, combos: 7, cost: 350, maxLines: 5, grossMax: 41250, netMax: 40900 },
                            { support: 8, combos: 8, cost: 400, maxLines: 5, grossMax: 41250, netMax: 40850 },
                            { support: 9, combos: 9, cost: 450, maxLines: 5, grossMax: 41250, netMax: 40800 },
                            { support: 10, combos: 10, cost: 500, maxLines: 5, grossMax: 41250, netMax: 40750 },
                            { support: 11, combos: 11, cost: 550, maxLines: 5, grossMax: 41250, netMax: 40700 },
                            { support: 12, combos: 12, cost: 600, maxLines: 5, grossMax: 41250, netMax: 40650 },
                            { support: 13, combos: 13, cost: 650, maxLines: 5, grossMax: 41250, netMax: 40600 },
                            { support: 14, combos: 14, cost: 700, maxLines: 5, grossMax: 41250, netMax: 40550 },
                            { support: 15, combos: 15, cost: 750, maxLines: 5, grossMax: 41250, netMax: 40450 },
                            { support: 16, combos: 16, cost: 800, maxLines: 5, grossMax: 41250, netMax: 40450 },
                            { support: 17, combos: 17, cost: 850, maxLines: 5, grossMax: 41250, netMax: 40400 },
                            { support: 18, combos: 18, cost: 900, maxLines: 5, grossMax: 41250, netMax: 40350 },
                            { support: 19, combos: 19, cost: 950, maxLines: 5, grossMax: 41250, netMax: 40300 },
                            { support: 20, combos: 20, cost: 1000, maxLines: 5, grossMax: 41250, netMax: 40250 },
                        ]
                    }
                },

                // ==================== EXTRA 6-3 ====================
                extra63: {
                    title: 'Banker - Extra 6-3',
                    odds: [
                        { match: '3 numbers', multiplier: 1550 },
                        { match: '2 numbers', multiplier: 32 },
                    ],
                    fixBankerTable: {
                        fixCount: 2,
                        rows: [
                            { support: 2, combos: 2, cost: 100, maxLines: 2, grossMax: 155000, netMax: 154900 },
                            { support: 3, combos: 3, cost: 150, maxLines: 3, grossMax: 232500, netMax: 232350 },
                            { support: 4, combos: 4, cost: 200, maxLines: 4, grossMax: 310000, netMax: 309800 },
                            { support: 5, combos: 5, cost: 250, maxLines: 4, grossMax: 310000, netMax: 309750 },
                            { support: 6, combos: 6, cost: 300, maxLines: 4, grossMax: 310000, netMax: 309700 },
                            { support: 7, combos: 7, cost: 350, maxLines: 4, grossMax: 310000, netMax: 309650 },
                            { support: 8, combos: 8, cost: 400, maxLines: 4, grossMax: 310000, netMax: 309600 },
                            { support: 9, combos: 9, cost: 450, maxLines: 4, grossMax: 310000, netMax: 309550 },
                            { support: 10, combos: 10, cost: 500, maxLines: 4, grossMax: 310000, netMax: 309500 },
                            { support: 11, combos: 11, cost: 550, maxLines: 4, grossMax: 310000, netMax: 309450 },
                            { support: 12, combos: 12, cost: 600, maxLines: 4, grossMax: 310000, netMax: 309400 },
                            { support: 13, combos: 13, cost: 650, maxLines: 4, grossMax: 310000, netMax: 309350 },
                            { support: 14, combos: 14, cost: 700, maxLines: 4, grossMax: 310000, netMax: 309300 },
                            { support: 15, combos: 15, cost: 750, maxLines: 4, grossMax: 310000, netMax: 309250 },
                            { support: 16, combos: 16, cost: 800, maxLines: 4, grossMax: 310000, netMax: 309200 },
                            { support: 17, combos: 17, cost: 850, maxLines: 4, grossMax: 310000, netMax: 309150 },
                            { support: 18, combos: 18, cost: 900, maxLines: 4, grossMax: 310000, netMax: 309100 },
                            { support: 19, combos: 19, cost: 950, maxLines: 4, grossMax: 310000, netMax: 309050 },
                            { support: 20, combos: 20, cost: 1000, maxLines: 4, grossMax: 310000, netMax: 309000 },
                        ]
                    }
                },

                // ==================== EXTRA 6-4 ====================
                extra64: {
                    title: 'Banker - Extra 6-4',
                    odds: [
                        { match: '4 numbers', multiplier: 11000 },
                        { match: '3 numbers', multiplier: 310 },
                        { match: '2 numbers', multiplier: 16 },
                    ],
                    fixBankerTable: {
                        fixCount: 3,
                        rows: [
                            { support: 2, combos: 2, cost: 100, maxLines: 2, grossMax: 1100000, netMax: 1099900 },
                            { support: 3, combos: 3, cost: 150, maxLines: 3, grossMax: 1650000, netMax: 1649850 },
                            { support: 4, combos: 4, cost: 200, maxLines: 3, grossMax: 1650000, netMax: 1649800 },
                            { support: 5, combos: 5, cost: 250, maxLines: 3, grossMax: 1650000, netMax: 1649750 },
                            { support: 6, combos: 6, cost: 300, maxLines: 3, grossMax: 1650000, netMax: 1649700 },
                            { support: 7, combos: 7, cost: 350, maxLines: 3, grossMax: 1650000, netMax: 1649650 },
                            { support: 8, combos: 8, cost: 400, maxLines: 3, grossMax: 1650000, netMax: 1649600 },
                            { support: 9, combos: 9, cost: 450, maxLines: 3, grossMax: 1650000, netMax: 1649550 },
                            { support: 10, combos: 10, cost: 500, maxLines: 3, grossMax: 1650000, netMax: 1649500 },
                            { support: 11, combos: 11, cost: 550, maxLines: 3, grossMax: 1650000, netMax: 1649450 },
                            { support: 12, combos: 12, cost: 600, maxLines: 3, grossMax: 1650000, netMax: 1649400 },
                            { support: 13, combos: 13, cost: 650, maxLines: 3, grossMax: 1650000, netMax: 1649350 },
                            { support: 14, combos: 14, cost: 700, maxLines: 3, grossMax: 1650000, netMax: 1649300 },
                            { support: 15, combos: 15, cost: 750, maxLines: 3, grossMax: 1650000, netMax: 1649250 },
                            { support: 16, combos: 16, cost: 800, maxLines: 3, grossMax: 1650000, netMax: 1649200 },
                            { support: 17, combos: 17, cost: 850, maxLines: 3, grossMax: 1650000, netMax: 1649150 },
                            { support: 18, combos: 18, cost: 900, maxLines: 3, grossMax: 1650000, netMax: 1649100 },
                            { support: 19, combos: 19, cost: 950, maxLines: 3, grossMax: 1650000, netMax: 1649050 },
                            { support: 20, combos: 20, cost: 1000, maxLines: 3, grossMax: 1650000, netMax: 1649000 },
                        ]
                    }
                },

                // ==================== EXTRA 6-5 ====================
                extra65: {
                    title: 'Banker - Extra 6-5',
                    odds: [
                        { match: '5 numbers', multiplier: 110000 },
                        { match: '4 numbers', multiplier: 5500 },
                        { match: '3 numbers', multiplier: 110 },
                        { match: '2 numbers', multiplier: 8 },
                    ],
                    fixBankerTable: {
                        fixCount: 4,
                        rows: [
                            { support: 2, combos: 2, cost: 100, maxLines: 2, grossMax: 11000000, netMax: 10999900 },
                            { support: 3, combos: 3, cost: 150, maxLines: 2, grossMax: 11000000, netMax: 10999850 },
                            { support: 4, combos: 4, cost: 200, maxLines: 2, grossMax: 11000000, netMax: 10999800 },
                            { support: 5, combos: 5, cost: 250, maxLines: 2, grossMax: 11000000, netMax: 10999750 },
                            { support: 6, combos: 6, cost: 300, maxLines: 2, grossMax: 11000000, netMax: 10999700 },
                            { support: 7, combos: 7, cost: 350, maxLines: 2, grossMax: 11000000, netMax: 10999650 },
                            { support: 8, combos: 8, cost: 400, maxLines: 2, grossMax: 11000000, netMax: 10999600 },
                            { support: 9, combos: 9, cost: 450, maxLines: 2, grossMax: 11000000, netMax: 10999550 },
                            { support: 10, combos: 10, cost: 500, maxLines: 2, grossMax: 11000000, netMax: 10999500 },
                            { support: 11, combos: 11, cost: 550, maxLines: 2, grossMax: 11000000, netMax: 10999450 },
                            { support: 12, combos: 12, cost: 600, maxLines: 2, grossMax: 11000000, netMax: 10999400 },
                            { support: 13, combos: 13, cost: 650, maxLines: 2, grossMax: 11000000, netMax: 10999350 },
                            { support: 14, combos: 14, cost: 700, maxLines: 2, grossMax: 11000000, netMax: 10999300 },
                            { support: 15, combos: 15, cost: 750, maxLines: 2, grossMax: 11000000, netMax: 10999250 },
                            { support: 16, combos: 16, cost: 800, maxLines: 2, grossMax: 11000000, netMax: 10999200 },
                            { support: 17, combos: 17, cost: 850, maxLines: 2, grossMax: 11000000, netMax: 10999150 },
                            { support: 18, combos: 18, cost: 900, maxLines: 2, grossMax: 11000000, netMax: 10999100 },
                            { support: 19, combos: 19, cost: 950, maxLines: 2, grossMax: 11000000, netMax: 10999050 },
                            { support: 20, combos: 20, cost: 1000, maxLines: 2, grossMax: 11000000, netMax: 10999000 },
                        ]
                    }
                },

                // ==================== DC EXTRA 6-2 ====================
                dcExtra62: {
                    title: 'Banker - DC Extra 6-2',
                    hasDualOdds: true,
                    odds: [
                        { match: '2 numbers', first5: '100', last5: '65' },
                    ],
                    fixBankerTable: {
                        fixCount: 1,
                        rows: [
                            { support: 2, combos: 2, cost: 100, maxLines: 2, grossMax: 10000, netMax: 9900 },
                            { support: 3, combos: 3, cost: 150, maxLines: 3, grossMax: 15000, netMax: 14850 },
                            { support: 4, combos: 4, cost: 200, maxLines: 4, grossMax: 20000, netMax: 19800 },
                            { support: 5, combos: 5, cost: 250, maxLines: 5, grossMax: 25000, netMax: 24750 },
                            { support: 6, combos: 6, cost: 300, maxLines: 5, grossMax: 25000, netMax: 24700 },
                            { support: 7, combos: 7, cost: 350, maxLines: 5, grossMax: 25000, netMax: 24650 },
                            { support: 8, combos: 8, cost: 400, maxLines: 5, grossMax: 25000, netMax: 24600 },
                            { support: 9, combos: 9, cost: 450, maxLines: 5, grossMax: 25000, netMax: 24550 },
                            { support: 10, combos: 10, cost: 500, maxLines: 5, grossMax: 25000, netMax: 24500 },
                            { support: 11, combos: 11, cost: 550, maxLines: 5, grossMax: 25000, netMax: 24450 },
                            { support: 12, combos: 12, cost: 600, maxLines: 5, grossMax: 25000, netMax: 24400 },
                            { support: 13, combos: 13, cost: 650, maxLines: 5, grossMax: 25000, netMax: 24350 },
                            { support: 14, combos: 14, cost: 700, maxLines: 5, grossMax: 25000, netMax: 24300 },
                            { support: 15, combos: 15, cost: 750, maxLines: 5, grossMax: 25000, netMax: 24250 },
                            { support: 16, combos: 16, cost: 800, maxLines: 5, grossMax: 25000, netMax: 24200 },
                            { support: 17, combos: 17, cost: 850, maxLines: 5, grossMax: 25000, netMax: 24150 },
                            { support: 18, combos: 18, cost: 900, maxLines: 5, grossMax: 25000, netMax: 24100 },
                            { support: 19, combos: 19, cost: 950, maxLines: 5, grossMax: 25000, netMax: 24050 },
                            { support: 20, combos: 20, cost: 1000, maxLines: 5, grossMax: 25000, netMax: 24000 },
                        ]
                    }
                },

                // ==================== DC EXTRA 6-3 ====================
                dcExtra63: {
                    title: 'Banker - DC Extra 6-3',
                    hasDualOdds: true,
                    odds: [
                        { match: '3 numbers', first5: '930', last5: '620' },
                        { match: '2 numbers', first5: '20', last5: '12' },
                    ],
                    fixBankerTable: {
                        fixCount: 2,
                        rows: [
                            { support: 2, combos: 2, cost: 100, maxLines: 2, grossMax: 93000, netMax: 92900 },
                            { support: 3, combos: 3, cost: 150, maxLines: 3, grossMax: 139500, netMax: 139350 },
                            { support: 4, combos: 4, cost: 200, maxLines: 4, grossMax: 186000, netMax: 185800 },
                            { support: 5, combos: 5, cost: 250, maxLines: 4, grossMax: 186000, netMax: 185750 },
                            { support: 6, combos: 6, cost: 300, maxLines: 4, grossMax: 186000, netMax: 185700 },
                            { support: 7, combos: 7, cost: 350, maxLines: 4, grossMax: 186000, netMax: 185650 },
                            { support: 8, combos: 8, cost: 400, maxLines: 4, grossMax: 186000, netMax: 185600 },
                            { support: 9, combos: 9, cost: 450, maxLines: 4, grossMax: 186000, netMax: 185550 },
                            { support: 10, combos: 10, cost: 500, maxLines: 4, grossMax: 186000, netMax: 185500 },
                            { support: 11, combos: 11, cost: 550, maxLines: 4, grossMax: 186000, netMax: 185450 },
                            { support: 12, combos: 12, cost: 600, maxLines: 4, grossMax: 186000, netMax: 185400 },
                            { support: 13, combos: 13, cost: 650, maxLines: 4, grossMax: 186000, netMax: 185350 },
                            { support: 14, combos: 14, cost: 700, maxLines: 4, grossMax: 186000, netMax: 185300 },
                            { support: 15, combos: 15, cost: 750, maxLines: 4, grossMax: 186000, netMax: 185250 },
                            { support: 16, combos: 16, cost: 800, maxLines: 4, grossMax: 186000, netMax: 185200 },
                            { support: 17, combos: 17, cost: 850, maxLines: 4, grossMax: 186000, netMax: 185150 },
                            { support: 18, combos: 18, cost: 900, maxLines: 4, grossMax: 186000, netMax: 185100 },
                            { support: 19, combos: 19, cost: 950, maxLines: 4, grossMax: 186000, netMax: 185050 },
                            { support: 20, combos: 20, cost: 1000, maxLines: 4, grossMax: 186000, netMax: 185000 },
                        ]
                    }
                },

                // ==================== DC EXTRA 6-4 ====================
                dcExtra64: {
                    title: 'Banker - DC Extra 6-4',
                    hasDualOdds: true,
                    odds: [
                        { match: '4 numbers', first5: '6,600', last5: '4,400' },
                        { match: '3 numbers', first5: '185', last5: '125' },
                        { match: '2 numbers', first5: '10', last5: '6' },
                    ],
                    fixBankerTable: {
                        fixCount: 3,
                        rows: [
                            { support: 2, combos: 2, cost: 100, maxLines: 2, grossMax: 660000, netMax: 659900 },
                            { support: 3, combos: 3, cost: 150, maxLines: 3, grossMax: 990000, netMax: 989850 },
                            { support: 4, combos: 4, cost: 200, maxLines: 3, grossMax: 990000, netMax: 989800 },
                            { support: 5, combos: 5, cost: 250, maxLines: 3, grossMax: 990000, netMax: 989750 },
                            { support: 6, combos: 6, cost: 300, maxLines: 3, grossMax: 990000, netMax: 989700 },
                            { support: 7, combos: 7, cost: 350, maxLines: 3, grossMax: 990000, netMax: 989650 },
                            { support: 8, combos: 8, cost: 400, maxLines: 3, grossMax: 990000, netMax: 989600 },
                            { support: 9, combos: 9, cost: 450, maxLines: 3, grossMax: 990000, netMax: 989550 },
                            { support: 10, combos: 10, cost: 500, maxLines: 3, grossMax: 990000, netMax: 989500 },
                            { support: 11, combos: 11, cost: 550, maxLines: 3, grossMax: 990000, netMax: 989450 },
                            { support: 12, combos: 12, cost: 600, maxLines: 3, grossMax: 990000, netMax: 989400 },
                            { support: 13, combos: 13, cost: 650, maxLines: 3, grossMax: 990000, netMax: 989350 },
                            { support: 14, combos: 14, cost: 700, maxLines: 3, grossMax: 990000, netMax: 989300 },
                            { support: 15, combos: 15, cost: 750, maxLines: 3, grossMax: 990000, netMax: 989250 },
                            { support: 16, combos: 16, cost: 800, maxLines: 3, grossMax: 990000, netMax: 989200 },
                            { support: 17, combos: 17, cost: 850, maxLines: 3, grossMax: 990000, netMax: 989150 },
                            { support: 18, combos: 18, cost: 900, maxLines: 3, grossMax: 990000, netMax: 989100 },
                            { support: 19, combos: 19, cost: 950, maxLines: 3, grossMax: 990000, netMax: 989050 },
                            { support: 20, combos: 20, cost: 1000, maxLines: 3, grossMax: 990000, netMax: 989000 },
                        ]
                    }
                },

                // ==================== DC EXTRA 6-5 ====================
                dcExtra65: {
                    title: 'Banker - DC Extra 6-5',
                    hasDualOdds: true,
                    odds: [
                        { match: '5 numbers', first5: '66,000', last5: '44,000' },
                        { match: '4 numbers', first5: '3,300', last5: '2,200' },
                        { match: '3 numbers', first5: '70', last5: '40' },
                        { match: '2 numbers', first5: '5', last5: '3' },
                    ],
                    fixBankerTable: {
                        fixCount: 4,
                        rows: [
                            { support: 2, combos: 2, cost: 100, maxLines: 2, grossMax: 6600000, netMax: 6599900 },
                            { support: 3, combos: 3, cost: 150, maxLines: 2, grossMax: 6600000, netMax: 6599850 },
                            { support: 4, combos: 4, cost: 200, maxLines: 2, grossMax: 6600000, netMax: 6599800 },
                            { support: 5, combos: 5, cost: 250, maxLines: 2, grossMax: 6600000, netMax: 6599750 },
                            { support: 6, combos: 6, cost: 300, maxLines: 2, grossMax: 6600000, netMax: 6599700 },
                            { support: 7, combos: 7, cost: 350, maxLines: 2, grossMax: 6600000, netMax: 6599650 },
                            { support: 8, combos: 8, cost: 400, maxLines: 2, grossMax: 6600000, netMax: 6599600 },
                            { support: 9, combos: 9, cost: 450, maxLines: 2, grossMax: 6600000, netMax: 6599550 },
                            { support: 10, combos: 10, cost: 500, maxLines: 2, grossMax: 6600000, netMax: 6599500 },
                            { support: 11, combos: 11, cost: 550, maxLines: 2, grossMax: 6600000, netMax: 6599450 },
                            { support: 12, combos: 12, cost: 600, maxLines: 2, grossMax: 6600000, netMax: 6599400 },
                            { support: 13, combos: 13, cost: 650, maxLines: 2, grossMax: 6600000, netMax: 6599350 },
                            { support: 14, combos: 14, cost: 700, maxLines: 2, grossMax: 6600000, netMax: 6599300 },
                            { support: 15, combos: 15, cost: 750, maxLines: 2, grossMax: 6600000, netMax: 6599250 },
                            { support: 16, combos: 16, cost: 800, maxLines: 2, grossMax: 6600000, netMax: 6599200 },
                            { support: 17, combos: 17, cost: 850, maxLines: 2, grossMax: 6600000, netMax: 6599150 },
                            { support: 18, combos: 18, cost: 900, maxLines: 2, grossMax: 6600000, netMax: 6599100 },
                            { support: 19, combos: 19, cost: 950, maxLines: 2, grossMax: 6600000, netMax: 6599050 },
                            { support: 20, combos: 20, cost: 1000, maxLines: 2, grossMax: 6600000, netMax: 6599000 },
                        ]
                    }
                },

                // ==================== EXTRA 7-2 ====================
                extra72: {
                    title: 'Banker - Extra 7-2',
                    odds: [
                        { match: '2 numbers', multiplier: 125 },
                    ],
                    fixBankerTable: {
                        fixCount: 1,
                        rows: [
                            { support: 2, combos: 2, cost: 100, maxLines: 2, grossMax: 12500, netMax: 12400 },
                            { support: 3, combos: 3, cost: 150, maxLines: 3, grossMax: 18750, netMax: 18600 },
                            { support: 4, combos: 4, cost: 200, maxLines: 4, grossMax: 25000, netMax: 24800 },
                            { support: 5, combos: 5, cost: 250, maxLines: 5, grossMax: 31250, netMax: 31000 },
                            { support: 6, combos: 6, cost: 300, maxLines: 6, grossMax: 37500, netMax: 37200 },
                            { support: 7, combos: 7, cost: 350, maxLines: 6, grossMax: 37500, netMax: 37150 },
                            { support: 8, combos: 8, cost: 400, maxLines: 6, grossMax: 37500, netMax: 37100 },
                            { support: 9, combos: 9, cost: 450, maxLines: 6, grossMax: 37500, netMax: 37050 },
                            { support: 10, combos: 10, cost: 500, maxLines: 6, grossMax: 37500, netMax: 37000 },
                            { support: 11, combos: 11, cost: 550, maxLines: 6, grossMax: 37500, netMax: 36950 },
                            { support: 12, combos: 12, cost: 600, maxLines: 6, grossMax: 37500, netMax: 36900 },
                            { support: 13, combos: 13, cost: 650, maxLines: 6, grossMax: 37500, netMax: 36850 },
                            { support: 14, combos: 14, cost: 700, maxLines: 6, grossMax: 37500, netMax: 36800 },
                            { support: 15, combos: 15, cost: 750, maxLines: 6, grossMax: 37500, netMax: 36750 },
                            { support: 16, combos: 16, cost: 800, maxLines: 6, grossMax: 37500, netMax: 36700 },
                            { support: 17, combos: 17, cost: 850, maxLines: 6, grossMax: 37500, netMax: 36650 },
                            { support: 18, combos: 18, cost: 900, maxLines: 6, grossMax: 37500, netMax: 36600 },
                            { support: 19, combos: 19, cost: 950, maxLines: 6, grossMax: 37500, netMax: 36550 },
                            { support: 20, combos: 20, cost: 1000, maxLines: 6, grossMax: 37500, netMax: 36500 },
                        ]
                    }
                },

                // ==================== EXTRA 7-3 ====================
                extra73: {
                    title: 'Banker - Extra 7-3',
                    odds: [
                        { match: '3 numbers', multiplier: 1250 },
                        { match: '2 numbers', multiplier: 16 },
                    ],
                    fixBankerTable: {
                        fixCount: 2,
                        rows: [
                            { support: 2, combos: 2, cost: 100, maxLines: 2, grossMax: 125000, netMax: 124900 },
                            { support: 3, combos: 3, cost: 150, maxLines: 3, grossMax: 187500, netMax: 187350 },
                            { support: 4, combos: 4, cost: 200, maxLines: 4, grossMax: 250000, netMax: 249800 },
                            { support: 5, combos: 5, cost: 250, maxLines: 5, grossMax: 312500, netMax: 312250 },
                            { support: 6, combos: 6, cost: 300, maxLines: 5, grossMax: 312500, netMax: 312200 },
                            { support: 7, combos: 7, cost: 350, maxLines: 5, grossMax: 312500, netMax: 312150 },
                            { support: 8, combos: 8, cost: 400, maxLines: 5, grossMax: 312500, netMax: 312100 },
                            { support: 9, combos: 9, cost: 450, maxLines: 5, grossMax: 312500, netMax: 312050 },
                            { support: 10, combos: 10, cost: 500, maxLines: 5, grossMax: 312500, netMax: 312000 },
                            { support: 11, combos: 11, cost: 550, maxLines: 5, grossMax: 312500, netMax: 311950 },
                            { support: 12, combos: 12, cost: 600, maxLines: 5, grossMax: 312500, netMax: 311900 },
                            { support: 13, combos: 13, cost: 650, maxLines: 5, grossMax: 312500, netMax: 311850 },
                            { support: 14, combos: 14, cost: 700, maxLines: 5, grossMax: 312500, netMax: 311800 },
                            { support: 15, combos: 15, cost: 750, maxLines: 5, grossMax: 312500, netMax: 311750 },
                            { support: 16, combos: 16, cost: 800, maxLines: 5, grossMax: 312500, netMax: 311700 },
                            { support: 17, combos: 17, cost: 850, maxLines: 5, grossMax: 312500, netMax: 311650 },
                            { support: 18, combos: 18, cost: 900, maxLines: 5, grossMax: 312500, netMax: 311600 },
                            { support: 19, combos: 19, cost: 950, maxLines: 5, grossMax: 312500, netMax: 311550 },
                            { support: 20, combos: 20, cost: 1000, maxLines: 5, grossMax: 312500, netMax: 311500 },
                        ]
                    }
                },

                // ==================== EXTRA 7-4 ====================
                extra74: {
                    title: 'Banker - Extra 7-4',
                    odds: [
                        { match: '4 numbers', multiplier: 6100 },
                        { match: '3 numbers', multiplier: 180 },
                        { match: '2 numbers', multiplier: 12 },
                    ],
                    fixBankerTable: {
                        fixCount: 3,
                        rows: [
                            { support: 2, combos: 2, cost: 100, maxLines: 2, grossMax: 610000, netMax: 609900 },
                            { support: 3, combos: 3, cost: 150, maxLines: 3, grossMax: 915000, netMax: 914850 },
                            { support: 4, combos: 4, cost: 200, maxLines: 4, grossMax: 1220000, netMax: 1219800 },
                            { support: 5, combos: 5, cost: 250, maxLines: 4, grossMax: 1220000, netMax: 1219750 },
                            { support: 6, combos: 6, cost: 300, maxLines: 4, grossMax: 1220000, netMax: 1219700 },
                            { support: 7, combos: 7, cost: 350, maxLines: 4, grossMax: 1220000, netMax: 1219650 },
                            { support: 8, combos: 8, cost: 400, maxLines: 4, grossMax: 1220000, netMax: 1219600 },
                            { support: 9, combos: 9, cost: 450, maxLines: 4, grossMax: 1220000, netMax: 1219550 },
                            { support: 10, combos: 10, cost: 500, maxLines: 4, grossMax: 1220000, netMax: 1219500 },
                            { support: 11, combos: 11, cost: 550, maxLines: 4, grossMax: 1220000, netMax: 1219450 },
                            { support: 12, combos: 12, cost: 600, maxLines: 4, grossMax: 1220000, netMax: 1219400 },
                            { support: 13, combos: 13, cost: 650, maxLines: 4, grossMax: 1220000, netMax: 1219350 },
                            { support: 14, combos: 14, cost: 700, maxLines: 4, grossMax: 1220000, netMax: 1219300 },
                            { support: 15, combos: 15, cost: 750, maxLines: 4, grossMax: 1220000, netMax: 1219250 },
                            { support: 16, combos: 16, cost: 800, maxLines: 4, grossMax: 1220000, netMax: 1219200 },
                            { support: 17, combos: 17, cost: 850, maxLines: 4, grossMax: 1220000, netMax: 1219150 },
                            { support: 18, combos: 18, cost: 900, maxLines: 4, grossMax: 1220000, netMax: 1219100 },
                            { support: 19, combos: 19, cost: 950, maxLines: 4, grossMax: 1220000, netMax: 1219050 },
                            { support: 20, combos: 20, cost: 1000, maxLines: 4, grossMax: 1220000, netMax: 1219000 },
                        ]
                    }
                },

                // ==================== EXTRA 7-5 ====================
                extra75: {
                    title: 'Banker - Extra 7-5',
                    odds: [
                        { match: '5 numbers', multiplier: 55000 },
                        { match: '4 numbers', multiplier: 3100 },
                        { match: '3 numbers', multiplier: 40 },
                        { match: '2 numbers', multiplier: 6 },
                    ],
                    fixBankerTable: {
                        fixCount: 4,
                        rows: [
                            { support: 2, combos: 2, cost: 100, maxLines: 2, grossMax: 5500000, netMax: 5499900 },
                            { support: 3, combos: 3, cost: 150, maxLines: 3, grossMax: 8250000, netMax: 8249850 },
                            { support: 4, combos: 4, cost: 200, maxLines: 3, grossMax: 8250000, netMax: 8249800 },
                            { support: 5, combos: 5, cost: 250, maxLines: 3, grossMax: 8250000, netMax: 8249750 },
                            { support: 6, combos: 6, cost: 300, maxLines: 3, grossMax: 8250000, netMax: 8249700 },
                            { support: 7, combos: 7, cost: 350, maxLines: 3, grossMax: 8250000, netMax: 8249650 },
                            { support: 8, combos: 8, cost: 400, maxLines: 3, grossMax: 8250000, netMax: 8249600 },
                            { support: 9, combos: 9, cost: 450, maxLines: 3, grossMax: 8250000, netMax: 8249550 },
                            { support: 10, combos: 10, cost: 500, maxLines: 3, grossMax: 8250000, netMax: 8249500 },
                            { support: 11, combos: 11, cost: 550, maxLines: 3, grossMax: 8250000, netMax: 8249450 },
                            { support: 12, combos: 12, cost: 600, maxLines: 3, grossMax: 8250000, netMax: 8249400 },
                            { support: 13, combos: 13, cost: 650, maxLines: 3, grossMax: 8250000, netMax: 8249350 },
                            { support: 14, combos: 14, cost: 700, maxLines: 3, grossMax: 8250000, netMax: 8249300 },
                            { support: 15, combos: 15, cost: 750, maxLines: 3, grossMax: 8250000, netMax: 8249250 },
                            { support: 16, combos: 16, cost: 800, maxLines: 3, grossMax: 8250000, netMax: 8249200 },
                            { support: 17, combos: 17, cost: 850, maxLines: 3, grossMax: 8250000, netMax: 8249150 },
                            { support: 18, combos: 18, cost: 900, maxLines: 3, grossMax: 8250000, netMax: 8249100 },
                            { support: 19, combos: 19, cost: 950, maxLines: 3, grossMax: 8250000, netMax: 8249050 },
                            { support: 20, combos: 20, cost: 1000, maxLines: 3, grossMax: 8250000, netMax: 8249000 },
                        ]
                    }
                },

                // ==================== DC EXTRA 7-2 ====================
                dcExtra72: {
                    title: 'Banker - DC Extra 7-2',
                    hasDualOdds: true,
                    odds: [
                        { match: '2 numbers', first5: '75', last5: '50' },
                    ],
                    fixBankerTable: {
                        fixCount: 1,
                        rows: [
                            { support: 2, combos: 2, cost: 100, maxLines: 2, grossMax: 7500, netMax: 7400 },
                            { support: 3, combos: 3, cost: 150, maxLines: 3, grossMax: 11250, netMax: 11100 },
                            { support: 4, combos: 4, cost: 200, maxLines: 4, grossMax: 15000, netMax: 14800 },
                            { support: 5, combos: 5, cost: 250, maxLines: 5, grossMax: 18750, netMax: 18500 },
                            { support: 6, combos: 6, cost: 300, maxLines: 6, grossMax: 22500, netMax: 22200 },
                            { support: 7, combos: 7, cost: 350, maxLines: 6, grossMax: 22500, netMax: 22150 },
                            { support: 8, combos: 8, cost: 400, maxLines: 6, grossMax: 22500, netMax: 22100 },
                            { support: 9, combos: 9, cost: 450, maxLines: 6, grossMax: 22500, netMax: 22050 },
                            { support: 10, combos: 10, cost: 500, maxLines: 6, grossMax: 22500, netMax: 22000 },
                            { support: 11, combos: 11, cost: 550, maxLines: 6, grossMax: 22500, netMax: 21950 },
                            { support: 12, combos: 12, cost: 600, maxLines: 6, grossMax: 22500, netMax: 21900 },
                            { support: 13, combos: 13, cost: 650, maxLines: 6, grossMax: 22500, netMax: 21850 },
                            { support: 14, combos: 14, cost: 700, maxLines: 6, grossMax: 22500, netMax: 21800 },
                            { support: 15, combos: 15, cost: 750, maxLines: 6, grossMax: 22500, netMax: 21750 },
                            { support: 16, combos: 16, cost: 800, maxLines: 6, grossMax: 22500, netMax: 21700 },
                            { support: 17, combos: 17, cost: 850, maxLines: 6, grossMax: 22500, netMax: 21650 },
                            { support: 18, combos: 18, cost: 900, maxLines: 6, grossMax: 22500, netMax: 21600 },
                            { support: 19, combos: 19, cost: 950, maxLines: 6, grossMax: 22500, netMax: 21550 },
                            { support: 20, combos: 20, cost: 1000, maxLines: 6, grossMax: 22500, netMax: 21500 },
                        ]
                    }
                },

                // ==================== DC EXTRA 7-3 ====================
                dcExtra73: {
                    title: 'Banker - DC Extra 7-3',
                    hasDualOdds: true,
                    odds: [
                        { match: '3 numbers', first5: '750', last5: '500' },
                        { match: '2 numbers', first5: '10', last5: '6' },
                    ],
                    fixBankerTable: {
                        fixCount: 2,
                        rows: [
                            { support: 2, combos: 2, cost: 100, maxLines: 2, grossMax: 75000, netMax: 74900 },
                            { support: 3, combos: 3, cost: 150, maxLines: 3, grossMax: 112500, netMax: 112350 },
                            { support: 4, combos: 4, cost: 200, maxLines: 4, grossMax: 150000, netMax: 149800 },
                            { support: 5, combos: 5, cost: 250, maxLines: 5, grossMax: 187500, netMax: 187250 },
                            { support: 6, combos: 6, cost: 300, maxLines: 5, grossMax: 187500, netMax: 187200 },
                            { support: 7, combos: 7, cost: 350, maxLines: 5, grossMax: 187500, netMax: 187150 },
                            { support: 8, combos: 8, cost: 400, maxLines: 5, grossMax: 187500, netMax: 187100 },
                            { support: 9, combos: 9, cost: 450, maxLines: 5, grossMax: 187500, netMax: 187050 },
                            { support: 10, combos: 10, cost: 500, maxLines: 5, grossMax: 187500, netMax: 187000 },
                            { support: 11, combos: 11, cost: 550, maxLines: 5, grossMax: 187500, netMax: 186950 },
                            { support: 12, combos: 12, cost: 600, maxLines: 5, grossMax: 187500, netMax: 186900 },
                            { support: 13, combos: 13, cost: 650, maxLines: 5, grossMax: 187500, netMax: 186850 },
                            { support: 14, combos: 14, cost: 700, maxLines: 5, grossMax: 187500, netMax: 186800 },
                            { support: 15, combos: 15, cost: 750, maxLines: 5, grossMax: 187500, netMax: 186750 },
                            { support: 16, combos: 16, cost: 800, maxLines: 5, grossMax: 187500, netMax: 186700 },
                            { support: 17, combos: 17, cost: 850, maxLines: 5, grossMax: 187500, netMax: 186650 },
                            { support: 18, combos: 18, cost: 900, maxLines: 5, grossMax: 187500, netMax: 186600 },
                            { support: 19, combos: 19, cost: 950, maxLines: 5, grossMax: 187500, netMax: 186550 },
                            { support: 20, combos: 20, cost: 1000, maxLines: 5, grossMax: 187500, netMax: 186500 },
                        ]
                    }
                },

                // ==================== DC EXTRA 7-4 ====================
                dcExtra74: {
                    title: 'Banker - DC Extra 7-4',
                    hasDualOdds: true,
                    odds: [
                        { match: '4 numbers', first5: '3,600', last5: '2,500' },
                        { match: '3 numbers', first5: '110', last5: '70' },
                        { match: '2 numbers', first5: '7', last5: '5' },
                    ],
                    fixBankerTable: {
                        fixCount: 3,
                        rows: [
                            { support: 2, combos: 2, cost: 100, maxLines: 2, grossMax: 360000, netMax: 359900 },
                            { support: 3, combos: 3, cost: 150, maxLines: 3, grossMax: 540000, netMax: 539850 },
                            { support: 4, combos: 4, cost: 200, maxLines: 4, grossMax: 720000, netMax: 719800 },
                            { support: 5, combos: 5, cost: 250, maxLines: 4, grossMax: 720000, netMax: 719750 },
                            { support: 6, combos: 6, cost: 300, maxLines: 4, grossMax: 720000, netMax: 719700 },
                            { support: 7, combos: 7, cost: 350, maxLines: 4, grossMax: 720000, netMax: 719650 },
                            { support: 8, combos: 8, cost: 400, maxLines: 4, grossMax: 720000, netMax: 719600 },
                            { support: 9, combos: 9, cost: 450, maxLines: 4, grossMax: 720000, netMax: 719550 },
                            { support: 10, combos: 10, cost: 500, maxLines: 4, grossMax: 720000, netMax: 719500 },
                            { support: 11, combos: 11, cost: 550, maxLines: 4, grossMax: 720000, netMax: 719450 },
                            { support: 12, combos: 12, cost: 600, maxLines: 4, grossMax: 720000, netMax: 719400 },
                            { support: 13, combos: 13, cost: 650, maxLines: 4, grossMax: 720000, netMax: 719350 },
                            { support: 14, combos: 14, cost: 700, maxLines: 4, grossMax: 720000, netMax: 719300 },
                            { support: 15, combos: 15, cost: 750, maxLines: 4, grossMax: 720000, netMax: 719250 },
                            { support: 16, combos: 16, cost: 800, maxLines: 4, grossMax: 720000, netMax: 719200 },
                            { support: 17, combos: 17, cost: 850, maxLines: 4, grossMax: 720000, netMax: 719150 },
                            { support: 18, combos: 18, cost: 900, maxLines: 4, grossMax: 720000, netMax: 719100 },
                            { support: 19, combos: 19, cost: 950, maxLines: 4, grossMax: 720000, netMax: 719050 },
                            { support: 20, combos: 20, cost: 1000, maxLines: 4, grossMax: 720000, netMax: 719000 },
                        ]
                    }
                },

                // ==================== DC EXTRA 7-5 ====================
                dcExtra75: {
                    title: 'Banker - DC Extra 7-5',
                    hasDualOdds: true,
                    odds: [
                        { match: '5 numbers', first5: '33,000', last5: '22,000' },
                        { match: '4 numbers', first5: '1,800', last5: '1,300' },
                        { match: '3 numbers', first5: '25', last5: '15' },
                        { match: '2 numbers', first5: '4', last5: '2' },
                    ],
                    fixBankerTable: {
                        fixCount: 4,
                        rows: [
                            { support: 2, combos: 2, cost: 100, maxLines: 2, grossMax: 3300000, netMax: 3299900 },
                            { support: 3, combos: 3, cost: 150, maxLines: 3, grossMax: 4950000, netMax: 4949850 },
                            { support: 4, combos: 4, cost: 200, maxLines: 3, grossMax: 4950000, netMax: 4949800 },
                            { support: 5, combos: 5, cost: 250, maxLines: 3, grossMax: 4950000, netMax: 4949750 },
                            { support: 6, combos: 6, cost: 300, maxLines: 3, grossMax: 4950000, netMax: 4949700 },
                            { support: 7, combos: 7, cost: 350, maxLines: 3, grossMax: 4950000, netMax: 4949650 },
                            { support: 8, combos: 8, cost: 400, maxLines: 3, grossMax: 4950000, netMax: 4949600 },
                            { support: 9, combos: 9, cost: 450, maxLines: 3, grossMax: 4950000, netMax: 4949550 },
                            { support: 10, combos: 10, cost: 500, maxLines: 3, grossMax: 4950000, netMax: 4949500 },
                            { support: 11, combos: 11, cost: 550, maxLines: 3, grossMax: 4950000, netMax: 4949450 },
                            { support: 12, combos: 12, cost: 600, maxLines: 3, grossMax: 4950000, netMax: 4949400 },
                            { support: 13, combos: 13, cost: 650, maxLines: 3, grossMax: 4950000, netMax: 4949350 },
                            { support: 14, combos: 14, cost: 700, maxLines: 3, grossMax: 4950000, netMax: 4949300 },
                            { support: 15, combos: 15, cost: 750, maxLines: 3, grossMax: 4950000, netMax: 4949250 },
                            { support: 16, combos: 16, cost: 800, maxLines: 3, grossMax: 4950000, netMax: 4949200 },
                            { support: 17, combos: 17, cost: 850, maxLines: 3, grossMax: 4950000, netMax: 4949150 },
                            { support: 18, combos: 18, cost: 900, maxLines: 3, grossMax: 4950000, netMax: 4949100 },
                            { support: 19, combos: 19, cost: 950, maxLines: 3, grossMax: 4950000, netMax: 4949050 },
                            { support: 20, combos: 20, cost: 1000, maxLines: 3, grossMax: 4950000, netMax: 4949000 },
                        ]
                    }
                },

            },
            // ==================== PERMUTATION GAMES ====================
            permutationGames: {
                // ==================== PERMUTATION CHANCE 2 ====================
                permChance2: {
                    title: 'Permutation - Chance 2',
                    odds: [
                        { match: '2 numbers', multiplier: 150 },
                        { match: '1 number + 1 bonus', multiplier: 15 },
                        { match: '0 number + 2 bonus', multiplier: 15 },
                        { match: '1 number', multiplier: 1 },
                        { match: '0 number + 1 bonus', multiplier: 1 },
                    ],
                    permutationTable: [
                        { numbers: 3, combos: 3, cost: 150, maxCombos: 3, grossMax: 22500, netMax: 22350 },
                        { numbers: 4, combos: 6, cost: 300, maxCombos: 6, grossMax: 45000, netMax: 44700 },
                        { numbers: 5, combos: 10, cost: 500, maxCombos: 10, grossMax: 75000, netMax: 74500 },
                        { numbers: 6, combos: 15, cost: 750, maxCombos: 15, grossMax: 78750, netMax: 78000 },
                        { numbers: 7, combos: 21, cost: 1050, maxCombos: 21, grossMax: 83250, netMax: 82200 },
                        { numbers: 8, combos: 28, cost: 1400, maxCombos: 21, grossMax: 83250, netMax: 81850 },
                        { numbers: 9, combos: 36, cost: 1800, maxCombos: 21, grossMax: 83250, netMax: 81450 },
                        { numbers: 10, combos: 45, cost: 2250, maxCombos: 21, grossMax: 83250, netMax: 81000 },
                        { numbers: 11, combos: 55, cost: 2750, maxCombos: 21, grossMax: 83250, netMax: 80500 },
                        { numbers: 12, combos: 66, cost: 3300, maxCombos: 21, grossMax: 83250, netMax: 79950 },
                        { numbers: 13, combos: 78, cost: 3900, maxCombos: 21, grossMax: 83250, netMax: 79350 },
                        { numbers: 14, combos: 91, cost: 4550, maxCombos: 21, grossMax: 83250, netMax: 78700 },
                        { numbers: 15, combos: 105, cost: 5250, maxCombos: 21, grossMax: 83250, netMax: 78000 },
                        { numbers: 16, combos: 120, cost: 6000, maxCombos: 21, grossMax: 83250, netMax: 77250 },
                        { numbers: 17, combos: 136, cost: 6800, maxCombos: 21, grossMax: 83250, netMax: 76450 },
                        { numbers: 18, combos: 153, cost: 7650, maxCombos: 21, grossMax: 83250, netMax: 75600 },
                        { numbers: 19, combos: 171, cost: 8550, maxCombos: 21, grossMax: 83250, netMax: 74700 },
                        { numbers: 20, combos: 190, cost: 9500, maxCombos: 21, grossMax: 83250, netMax: 73750 },
                    ]
                },

                // ==================== PERMUTATION TURBO 2 ====================
                permTurbo2: {
                    title: 'Permutation - Turbo 2',
                    odds: [
                        { match: '2 numbers', multiplier: 2400 },
                        { match: '1 number + 1 bonus', multiplier: 40 },
                        { match: '0 number + 2 bonus', multiplier: 40 },
                    ],
                    permutationTable: [
                        { numbers: 3, combos: 3, cost: 150, maxCombos: 3, grossMax: 124000, netMax: 123850 },
                        { numbers: 4, combos: 6, cost: 300, maxCombos: 6, grossMax: 130000, netMax: 129700 },
                        { numbers: 5, combos: 10, cost: 500, maxCombos: 6, grossMax: 130000, netMax: 129500 },
                        { numbers: 6, combos: 15, cost: 750, maxCombos: 6, grossMax: 130000, netMax: 129250 },
                        { numbers: 7, combos: 21, cost: 1050, maxCombos: 6, grossMax: 130000, netMax: 128950 },
                        { numbers: 8, combos: 28, cost: 1400, maxCombos: 6, grossMax: 130000, netMax: 128600 },
                        { numbers: 9, combos: 36, cost: 1800, maxCombos: 6, grossMax: 130000, netMax: 128200 },
                        { numbers: 10, combos: 45, cost: 2250, maxCombos: 6, grossMax: 130000, netMax: 127750 },
                        { numbers: 11, combos: 55, cost: 2750, maxCombos: 6, grossMax: 130000, netMax: 127250 },
                        { numbers: 12, combos: 66, cost: 3300, maxCombos: 6, grossMax: 130000, netMax: 126700 },
                        { numbers: 13, combos: 78, cost: 3900, maxCombos: 6, grossMax: 130000, netMax: 126100 },
                        { numbers: 14, combos: 91, cost: 4550, maxCombos: 6, grossMax: 130000, netMax: 125450 },
                        { numbers: 15, combos: 105, cost: 5250, maxCombos: 6, grossMax: 130000, netMax: 124750 },
                        { numbers: 16, combos: 120, cost: 6000, maxCombos: 6, grossMax: 130000, netMax: 124000 },
                        { numbers: 17, combos: 136, cost: 6800, maxCombos: 6, grossMax: 130000, netMax: 123200 },
                        { numbers: 18, combos: 153, cost: 7650, maxCombos: 6, grossMax: 130000, netMax: 122350 },
                        { numbers: 19, combos: 171, cost: 8550, maxCombos: 6, grossMax: 130000, netMax: 121450 },
                        { numbers: 20, combos: 190, cost: 9500, maxCombos: 6, grossMax: 130000, netMax: 120500 },
                    ]
                },

                // ==================== PERMUTATION TURBO 3 ====================
                permTurbo3: {
                    title: 'Permutation - Turbo 3',
                    odds: [
                        { match: '2 numbers', multiplier: 800 },
                        { match: '1 number + 1 bonus', multiplier: 30 },
                        { match: '0 number + 2 bonus', multiplier: 30 },
                    ],
                    permutationTable: [
                        { numbers: 3, combos: 3, cost: 150, maxCombos: 3, grossMax: 120000, netMax: 119850 },
                        { numbers: 4, combos: 6, cost: 300, maxCombos: 6, grossMax: 124500, netMax: 124200 },
                        { numbers: 5, combos: 10, cost: 500, maxCombos: 10, grossMax: 130500, netMax: 130000 },
                        { numbers: 6, combos: 15, cost: 750, maxCombos: 10, grossMax: 130500, netMax: 129750 },
                        { numbers: 7, combos: 21, cost: 1050, maxCombos: 10, grossMax: 130500, netMax: 129450 },
                        { numbers: 8, combos: 28, cost: 1400, maxCombos: 10, grossMax: 130500, netMax: 129100 },
                        { numbers: 9, combos: 36, cost: 1800, maxCombos: 10, grossMax: 130500, netMax: 128700 },
                        { numbers: 10, combos: 45, cost: 2250, maxCombos: 10, grossMax: 130500, netMax: 128250 },
                        { numbers: 11, combos: 55, cost: 2750, maxCombos: 10, grossMax: 130500, netMax: 127750 },
                        { numbers: 12, combos: 66, cost: 3300, maxCombos: 10, grossMax: 130500, netMax: 127200 },
                        { numbers: 13, combos: 78, cost: 3900, maxCombos: 10, grossMax: 130500, netMax: 126600 },
                        { numbers: 14, combos: 91, cost: 4550, maxCombos: 10, grossMax: 130500, netMax: 125950 },
                        { numbers: 15, combos: 105, cost: 5250, maxCombos: 10, grossMax: 130500, netMax: 125250 },
                        { numbers: 16, combos: 120, cost: 6000, maxCombos: 10, grossMax: 130500, netMax: 124500 },
                        { numbers: 17, combos: 136, cost: 6800, maxCombos: 10, grossMax: 130500, netMax: 123700 },
                        { numbers: 18, combos: 153, cost: 7650, maxCombos: 10, grossMax: 130500, netMax: 122850 },
                        { numbers: 19, combos: 171, cost: 8550, maxCombos: 10, grossMax: 130500, netMax: 121950 },
                        { numbers: 20, combos: 190, cost: 9500, maxCombos: 10, grossMax: 130500, netMax: 121000 },
                    ]
                },

                // ==================== PERMUTATION TURBO 4 ====================
                permTurbo4: {
                    title: 'Permutation - Turbo 4',
                    odds: [
                        { match: '2 numbers', multiplier: 400 },
                        { match: '1 number + 1 bonus', multiplier: 25 },
                        { match: '0 number + 2 bonus', multiplier: 25 },
                    ],
                    permutationTable: [
                        { numbers: 3, combos: 3, cost: 150, maxCombos: 3, grossMax: 60000, netMax: 59850 },
                        { numbers: 4, combos: 6, cost: 300, maxCombos: 6, grossMax: 120000, netMax: 119700 },
                        { numbers: 5, combos: 10, cost: 500, maxCombos: 10, grossMax: 125000, netMax: 124500 },
                        { numbers: 6, combos: 15, cost: 750, maxCombos: 15, grossMax: 131250, netMax: 130500 },
                        { numbers: 7, combos: 21, cost: 1050, maxCombos: 15, grossMax: 131250, netMax: 130200 },
                        { numbers: 8, combos: 28, cost: 1400, maxCombos: 15, grossMax: 131250, netMax: 129850 },
                        { numbers: 9, combos: 36, cost: 1800, maxCombos: 15, grossMax: 131250, netMax: 129450 },
                        { numbers: 10, combos: 45, cost: 2250, maxCombos: 15, grossMax: 131250, netMax: 129000 },
                        { numbers: 11, combos: 55, cost: 2750, maxCombos: 15, grossMax: 131250, netMax: 128500 },
                        { numbers: 12, combos: 66, cost: 3300, maxCombos: 15, grossMax: 131250, netMax: 127950 },
                        { numbers: 13, combos: 78, cost: 3900, maxCombos: 15, grossMax: 131250, netMax: 127350 },
                        { numbers: 14, combos: 91, cost: 4550, maxCombos: 15, grossMax: 131250, netMax: 126700 },
                        { numbers: 15, combos: 105, cost: 5250, maxCombos: 15, grossMax: 131250, netMax: 126000 },
                        { numbers: 16, combos: 120, cost: 6000, maxCombos: 15, grossMax: 131250, netMax: 125250 },
                        { numbers: 17, combos: 136, cost: 6800, maxCombos: 15, grossMax: 131250, netMax: 124450 },
                        { numbers: 18, combos: 153, cost: 7650, maxCombos: 15, grossMax: 131250, netMax: 123600 },
                        { numbers: 19, combos: 171, cost: 8550, maxCombos: 15, grossMax: 131250, netMax: 122700 },
                        { numbers: 20, combos: 190, cost: 9500, maxCombos: 15, grossMax: 131250, netMax: 121750 },
                    ]
                },

                // ==================== PERMUTATION TURBO 5 ====================
                permTurbo5: {
                    title: 'Permutation - Turbo 5',
                    odds: [
                        { match: '2 numbers', multiplier: 300 },
                        { match: '1 number + 1 bonus', multiplier: 12 },
                        { match: '0 number + 2 bonus', multiplier: 12 },
                    ],
                    permutationTable: [
                        { numbers: 3, combos: 3, cost: 150, maxCombos: 3, grossMax: 45000, netMax: 44850 },
                        { numbers: 4, combos: 6, cost: 300, maxCombos: 6, grossMax: 90000, netMax: 89700 },
                        { numbers: 5, combos: 10, cost: 500, maxCombos: 10, grossMax: 150000, netMax: 149500 },
                        { numbers: 6, combos: 15, cost: 750, maxCombos: 15, grossMax: 153000, netMax: 152250 },
                        { numbers: 7, combos: 21, cost: 1050, maxCombos: 21, grossMax: 156600, netMax: 155550 },
                        { numbers: 8, combos: 28, cost: 1400, maxCombos: 21, grossMax: 156600, netMax: 155200 },
                        { numbers: 9, combos: 36, cost: 1800, maxCombos: 21, grossMax: 156600, netMax: 154800 },
                        { numbers: 10, combos: 45, cost: 2250, maxCombos: 21, grossMax: 156600, netMax: 154350 },
                        { numbers: 11, combos: 55, cost: 2750, maxCombos: 21, grossMax: 156600, netMax: 153850 },
                        { numbers: 12, combos: 66, cost: 3300, maxCombos: 21, grossMax: 156600, netMax: 153300 },
                        { numbers: 13, combos: 78, cost: 3900, maxCombos: 21, grossMax: 156600, netMax: 152700 },
                        { numbers: 14, combos: 91, cost: 4550, maxCombos: 21, grossMax: 156600, netMax: 152050 },
                        { numbers: 15, combos: 105, cost: 5250, maxCombos: 21, grossMax: 156600, netMax: 151350 },
                        { numbers: 16, combos: 120, cost: 6000, maxCombos: 21, grossMax: 156600, netMax: 150600 },
                        { numbers: 17, combos: 136, cost: 6800, maxCombos: 21, grossMax: 156600, netMax: 149800 },
                        { numbers: 18, combos: 153, cost: 7650, maxCombos: 21, grossMax: 156600, netMax: 148950 },
                        { numbers: 19, combos: 171, cost: 8550, maxCombos: 21, grossMax: 156600, netMax: 148050 },
                        { numbers: 20, combos: 190, cost: 9500, maxCombos: 21, grossMax: 156600, netMax: 147100 },
                    ]
                },

                // ==================== PERMUTATION DC TURBO 2 ====================
                permDcTurbo2: {
                    title: 'Permutation - DC Turbo 2',
                    hasDualOdds: true,
                    odds: [
                        { match: '2 numbers', first5: '1,500', last5: '1,100' },
                    ],
                    permutationTable: [
                        { numbers: 3, combos: 3, cost: 150, maxCombos: 1, grossMax: 75000, netMax: 74850 },
                        { numbers: 4, combos: 6, cost: 300, maxCombos: 2, grossMax: 130000, netMax: 129700 },
                        { numbers: 5, combos: 10, cost: 500, maxCombos: 2, grossMax: 130000, netMax: 129500 },
                        { numbers: 6, combos: 15, cost: 750, maxCombos: 2, grossMax: 130000, netMax: 129250 },
                        { numbers: 7, combos: 21, cost: 1050, maxCombos: 2, grossMax: 130000, netMax: 128950 },
                        { numbers: 8, combos: 28, cost: 1400, maxCombos: 2, grossMax: 130000, netMax: 128600 },
                        { numbers: 9, combos: 36, cost: 1800, maxCombos: 2, grossMax: 130000, netMax: 128200 },
                        { numbers: 10, combos: 45, cost: 2250, maxCombos: 2, grossMax: 130000, netMax: 127750 },
                        { numbers: 11, combos: 55, cost: 2750, maxCombos: 2, grossMax: 130000, netMax: 127250 },
                        { numbers: 12, combos: 66, cost: 3300, maxCombos: 2, grossMax: 130000, netMax: 126700 },
                        { numbers: 13, combos: 78, cost: 3900, maxCombos: 2, grossMax: 130000, netMax: 126100 },
                        { numbers: 14, combos: 91, cost: 4550, maxCombos: 2, grossMax: 130000, netMax: 125450 },
                        { numbers: 15, combos: 105, cost: 5250, maxCombos: 2, grossMax: 130000, netMax: 124750 },
                        { numbers: 16, combos: 120, cost: 6000, maxCombos: 2, grossMax: 130000, netMax: 124000 },
                        { numbers: 17, combos: 136, cost: 6800, maxCombos: 2, grossMax: 130000, netMax: 123200 },
                        { numbers: 18, combos: 153, cost: 7650, maxCombos: 2, grossMax: 130000, netMax: 122350 },
                        { numbers: 19, combos: 171, cost: 8550, maxCombos: 2, grossMax: 130000, netMax: 121450 },
                        { numbers: 20, combos: 190, cost: 9500, maxCombos: 2, grossMax: 130000, netMax: 120500 },
                    ]
                },

                // ==================== PERMUTATION DC TURBO 3 ====================
                permDcTurbo3: {
                    title: 'Permutation - DC Turbo 3',
                    hasDualOdds: true,
                    odds: [
                        { match: '2 numbers', first5: '520', last5: '320' },
                    ],
                    permutationTable: [
                        { numbers: 3, combos: 3, cost: 150, maxCombos: 3, grossMax: 78000, netMax: 77850 },
                        { numbers: 4, combos: 6, cost: 300, maxCombos: 3, grossMax: 78000, netMax: 77700 },
                        { numbers: 5, combos: 10, cost: 500, maxCombos: 4, grossMax: 94000, netMax: 93500 },
                        { numbers: 6, combos: 15, cost: 750, maxCombos: 6, grossMax: 126000, netMax: 125250 },
                        { numbers: 7, combos: 21, cost: 1050, maxCombos: 6, grossMax: 126000, netMax: 124950 },
                        { numbers: 8, combos: 28, cost: 1400, maxCombos: 6, grossMax: 126000, netMax: 124600 },
                        { numbers: 9, combos: 36, cost: 1800, maxCombos: 6, grossMax: 126000, netMax: 124200 },
                        { numbers: 10, combos: 45, cost: 2250, maxCombos: 6, grossMax: 126000, netMax: 123750 },
                        { numbers: 11, combos: 55, cost: 2750, maxCombos: 6, grossMax: 126000, netMax: 123250 },
                        { numbers: 12, combos: 66, cost: 3300, maxCombos: 6, grossMax: 126000, netMax: 122700 },
                        { numbers: 13, combos: 78, cost: 3900, maxCombos: 6, grossMax: 126000, netMax: 122100 },
                        { numbers: 14, combos: 91, cost: 4550, maxCombos: 6, grossMax: 126000, netMax: 121450 },
                        { numbers: 15, combos: 105, cost: 5250, maxCombos: 6, grossMax: 126000, netMax: 120750 },
                        { numbers: 16, combos: 120, cost: 6000, maxCombos: 6, grossMax: 126000, netMax: 120000 },
                        { numbers: 17, combos: 136, cost: 6800, maxCombos: 6, grossMax: 126000, netMax: 119200 },
                        { numbers: 18, combos: 153, cost: 7650, maxCombos: 6, grossMax: 126000, netMax: 118350 },
                        { numbers: 19, combos: 171, cost: 8550, maxCombos: 6, grossMax: 126000, netMax: 117450 },
                        { numbers: 20, combos: 190, cost: 9500, maxCombos: 6, grossMax: 126000, netMax: 116500 },
                    ]
                },

                // ==================== PERMUTATION DC TURBO 4 ====================
                permDcTurbo4: {
                    title: 'Permutation - DC Turbo 4',
                    hasDualOdds: true,
                    odds: [
                        { match: '2 numbers', first5: '260', last5: '160' },
                    ],
                    permutationTable: [
                        { numbers: 3, combos: 3, cost: 150, maxCombos: 3, grossMax: 39000, netMax: 38850 },
                        { numbers: 4, combos: 6, cost: 300, maxCombos: 6, grossMax: 78000, netMax: 77700 },
                        { numbers: 5, combos: 10, cost: 500, maxCombos: 7, grossMax: 86000, netMax: 85500 },
                        { numbers: 6, combos: 15, cost: 750, maxCombos: 8, grossMax: 94000, netMax: 93250 },
                        { numbers: 7, combos: 21, cost: 1050, maxCombos: 10, grossMax: 110000, netMax: 108950 },
                        { numbers: 8, combos: 28, cost: 1400, maxCombos: 12, grossMax: 126000, netMax: 124600 },
                        { numbers: 9, combos: 36, cost: 1800, maxCombos: 12, grossMax: 126000, netMax: 124200 },
                        { numbers: 10, combos: 45, cost: 2250, maxCombos: 12, grossMax: 126000, netMax: 123750 },
                        { numbers: 11, combos: 55, cost: 2750, maxCombos: 12, grossMax: 126000, netMax: 123250 },
                        { numbers: 12, combos: 66, cost: 3300, maxCombos: 12, grossMax: 126000, netMax: 122700 },
                        { numbers: 13, combos: 78, cost: 3900, maxCombos: 12, grossMax: 126000, netMax: 122100 },
                        { numbers: 14, combos: 91, cost: 4550, maxCombos: 12, grossMax: 126000, netMax: 121450 },
                        { numbers: 15, combos: 105, cost: 5250, maxCombos: 12, grossMax: 126000, netMax: 120750 },
                        { numbers: 16, combos: 120, cost: 6000, maxCombos: 12, grossMax: 126000, netMax: 120000 },
                        { numbers: 17, combos: 136, cost: 6800, maxCombos: 12, grossMax: 126000, netMax: 119200 },
                        { numbers: 18, combos: 153, cost: 7650, maxCombos: 12, grossMax: 126000, netMax: 118350 },
                        { numbers: 19, combos: 171, cost: 8550, maxCombos: 12, grossMax: 126000, netMax: 117450 },
                        { numbers: 20, combos: 190, cost: 9500, maxCombos: 12, grossMax: 126000, netMax: 116500 },
                    ]
                },

                // ==================== PERMUTATION DC TURBO 5 ====================
                permDcTurbo5: {
                    title: 'Permutation - DC Turbo 5',
                    hasDualOdds: true,
                    odds: [
                        { match: '2 numbers', first5: '185', last5: '125' },
                    ],
                    permutationTable: [
                        { numbers: 3, combos: 3, cost: 150, maxCombos: 3, grossMax: 27750, netMax: 27600 },
                        { numbers: 4, combos: 6, cost: 300, maxCombos: 6, grossMax: 55500, netMax: 55200 },
                        { numbers: 5, combos: 10, cost: 500, maxCombos: 10, grossMax: 92500, netMax: 92000 },
                        { numbers: 6, combos: 15, cost: 750, maxCombos: 10, grossMax: 92500, netMax: 91750 },
                        { numbers: 7, combos: 21, cost: 1050, maxCombos: 11, grossMax: 98750, netMax: 97700 },
                        { numbers: 8, combos: 28, cost: 1400, maxCombos: 13, grossMax: 111250, netMax: 109850 },
                        { numbers: 9, combos: 36, cost: 1800, maxCombos: 16, grossMax: 130000, netMax: 128200 },
                        { numbers: 10, combos: 45, cost: 2250, maxCombos: 20, grossMax: 155000, netMax: 152750 },
                        { numbers: 11, combos: 55, cost: 2750, maxCombos: 20, grossMax: 155000, netMax: 152250 },
                        { numbers: 12, combos: 66, cost: 3300, maxCombos: 20, grossMax: 155000, netMax: 151700 },
                        { numbers: 13, combos: 78, cost: 3900, maxCombos: 20, grossMax: 155000, netMax: 151100 },
                        { numbers: 14, combos: 91, cost: 4550, maxCombos: 20, grossMax: 155000, netMax: 150450 },
                        { numbers: 15, combos: 105, cost: 5250, maxCombos: 20, grossMax: 155000, netMax: 149750 },
                        { numbers: 16, combos: 120, cost: 6000, maxCombos: 20, grossMax: 155000, netMax: 149000 },
                        { numbers: 17, combos: 136, cost: 6800, maxCombos: 20, grossMax: 155000, netMax: 148200 },
                        { numbers: 18, combos: 153, cost: 7650, maxCombos: 20, grossMax: 155000, netMax: 147350 },
                        { numbers: 19, combos: 171, cost: 8550, maxCombos: 20, grossMax: 155000, netMax: 146450 },
                        { numbers: 20, combos: 190, cost: 9500, maxCombos: 20, grossMax: 155000, netMax: 145500 },
                    ]
                },

                // More permutation games will be added here...
            }
        }
    },
    methods: {
        selectGame(type, key) {
            if (this.activeGame === key && this.activeType === type) {
                this.activeGame = null
                this.activeType = null
            } else {
                this.activeGame = key
                this.activeType = type
            }
        },
        formatNumber(num) {
            return num.toLocaleString('fr-FR')
        }
    }
}
</script>

<style lang="scss">
@import '~ThemePath/scss/payouts/_payout-detail.scss';

.payout-banker-permutation {
    &__header {
        margin-bottom: 24px;
        text-align: center;
        
        h2 {
            color: #1a1a2e;
            margin-bottom: 8px;
            font-size: 1.75rem;
        }
    }

    &__layout {
        display: flex;
        gap: 24px;
        min-height: 600px;

        @media (max-width: 768px) {
            flex-direction: column;
        }
    }

    &__sidebar {
        width: 200px;
        flex-shrink: 0;
        background: #f8f9fa;
        border-radius: 12px;
        overflow: hidden;
        border: 1px solid #e0e0e0;

        @media (max-width: 768px) {
            width: 100%;
        }

        &--permutation {
            .payout-banker-permutation__btn {
                &:hover {
                    border-color: #1976D2;
                    background: #E3F2FD;
                }
                
                &.active {
                    border-color: #1565C0;
                    background: linear-gradient(135deg, #2196F3 0%, #1565C0 100%);
                    color: white;
                }
            }
        }
    }

    &__sidebar-header {
        background: linear-gradient(135deg, #ff9800 0%, #e65100 100%);
        color: white;
        padding: 16px;
        text-align: center;

        h3 {
            margin: 0;
            font-size: 1.25rem;
            font-weight: 700;
            letter-spacing: 1px;
        }

        &--permutation {
            background: linear-gradient(135deg, #2196F3 0%, #1565C0 100%);
        }
    }

    &__sidebar-buttons {
        display: flex;
        flex-direction: column;
        padding: 8px;
        gap: 4px;
        max-height: 550px;
        overflow-y: auto;

        @media (max-width: 768px) {
            flex-direction: row;
            flex-wrap: wrap;
            max-height: none;
        }
    }

    &__content {
        flex: 1;
        min-width: 0;
    }

    &__btn {
        padding: 10px 12px;
        border: 1px solid #e0e0e0;
        border-radius: 6px;
        background: white;
        color: #495057;
        font-weight: 500;
        font-size: 0.85rem;
        cursor: pointer;
        transition: all 0.2s ease;
        text-align: left;
        white-space: nowrap;
        
        &:hover {
            border-color: #f57c00;
            background: #fff3e0;
        }
        
        &.active {
            border-color: #e65100;
            background: linear-gradient(135deg, #ff9800 0%, #e65100 100%);
            color: white;
        }

        @media (max-width: 768px) {
            flex: 1 1 auto;
            text-align: center;
        }
    }

    &__panel {
        background: white;
        border-radius: 12px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        overflow: hidden;
    }

    &__panel-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px 20px;
        background: linear-gradient(135deg, #ff9800 0%, #e65100 100%);
        color: white;

        h3 {
            margin: 0;
            font-size: 1.25rem;
        }

        &--permutation {
            background: linear-gradient(135deg, #2196F3 0%, #1565C0 100%);
        }
    }

    &__section {
        padding: 20px;
        border-bottom: 1px solid #eee;

        &:last-child {
            border-bottom: none;
        }

        h4 {
            color: #333;
            margin: 0 0 16px 0;
            font-size: 1.1rem;
            padding-bottom: 8px;
            border-bottom: 2px solid #ff9800;
            display: inline-block;
        }
    }

    &__info-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
        gap: 12px;

        .info-item {
            background: #f8f9fa;
            padding: 12px 16px;
            border-radius: 8px;
            text-align: center;

            &.highlight {
                background: linear-gradient(135deg, #fff3cd 0%, #ffeeba 100%);
            }

            .label {
                display: block;
                font-size: 0.8rem;
                color: #666;
                margin-bottom: 4px;
            }

            .value {
                display: block;
                font-size: 1.1rem;
                font-weight: 700;
                color: #1a1a2e;
            }
        }
    }

    &__table {
        width: 100%;
        border-collapse: collapse;
        font-size: 0.9rem;

        &.odds-table {
            max-width: 400px;

            &.dual-odds {
                max-width: 500px;
            }
        }

        .odds-note {
            color: #666;
            font-size: 0.85rem;
            margin-bottom: 10px;
        }

        th {
            background: linear-gradient(135deg, #343a40 0%, #212529 100%);
            color: white;
            padding: 12px;
            text-align: left;
            font-weight: 600;
        }

        td {
            padding: 10px 12px;
            border-bottom: 1px solid #eee;

            &.highlight {
                background: #fff3cd;
                font-weight: 700;
                color: #856404;
            }
        }

        tbody tr {
            transition: background 0.2s;

            &:hover {
                background: #f8f9fa;
            }

            &:nth-child(even) {
                background: #fafafa;

                &:hover {
                    background: #f0f0f0;
                }
            }
        }
    }

    &__empty {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        min-height: 400px;
        text-align: center;
        padding: 40px 20px;
        background: #f8f9fa;
        border-radius: 12px;
        border: 2px dashed #dee2e6;

        p {
            color: #6c757d;
            font-size: 1.1rem;
            margin: 0;
        }
    }
}

// Transition animations
.slide-fade-enter-active {
    transition: all 0.3s ease;
}

.slide-fade-leave-active {
    transition: all 0.2s ease;
}

.slide-fade-enter,
.slide-fade-leave-to {
    transform: translateY(-10px);
    opacity: 0;
}
</style>
